import { useState, useEffect } from "react";
import { GroupedNavigation } from "../components/navigation/GroupedNavigation";
import { groupArtistNavigation } from "../components/navigation/navigationGroups";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { CollectiblesManagement as DigitalCollectiblesManagement } from "../components/collectibles/CollectiblesManagement";
import { useImageEditor } from "../components/image-editor/ImageEditorProvider";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Music, 
  Calendar, 
  ShoppingBag, 
  User,
  LogOut,
  Plus,
  Loader2,
  Upload,
  Sparkles,
  Gift,
  PlayCircle,
  Ticket,
  TrendingUp,
  Menu,
  X,
  Heart,
  Users,
  DollarSign,
  Trash2,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  ExternalLink,
  CheckCircle,
  Package,
  AlertCircle,
  Banknote,
  Bell,
  BadgeCheck,
  Shield,
  CalendarRange,
  Tag,
  MessageCircle,
  Star,
  Video,
  Phone,
  Cpu,
  RefreshCw,
  CheckCircle2,
  ShieldCheck,
  ShieldAlert,
  CalendarDays,
  Landmark,
  Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import GrowthHub from "../components/GrowthHub";
import SecuritySettings from "../components/SecuritySettings";
import MantraSettings from "../components/MantraSettings";
import ProductSubcategoryFields from "../components/ProductSubcategoryFields";
import BasicProductDetails from "../components/BasicProductDetails";
import AdminMessageCallout from "../components/AdminMessageCallout";
import PromoKit from "../components/PromoKit";
import AdminInmailViewer from "../components/AdminInmailViewer";
import TrustSafetyPanel from "../components/TrustSafetyPanel";
import ReferralsTab from "../components/ReferralsTab";
import TeaserModal from "../components/TeaserModal";

// Dashboard Overview Component
const AdminInboxWidget = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/inbox/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setSummary(res.data);
      } catch {
        // Non-admins get 403 — component simply hides
        if (mounted) setSummary(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 60000); // auto-refresh every 60s
    return () => { mounted = false; clearInterval(interval); };
  }, [token]);

  if (loading || !summary) return null;

  const buckets = summary.buckets || {};
  const cards = [
    { key: "notifications", label: "Unread notifications", count: buckets.notifications || 0, to: "/dashboard/support", color: "#32D583" },
    { key: "recovery_cases", label: "Recovery cases", count: buckets.recovery_cases || 0, to: "/admin/awaiting-review?tab=recovery", color: "#60A5FA" },
    { key: "idol_applications_pending", label: "Idol applications", count: buckets.idol_applications_pending || 0, to: "/admin/idol-applications", color: "#FF3B3B" },
    { key: "corporate_applications_pending", label: "Sponsors", count: buckets.corporate_applications_pending || 0, to: "/admin/sponsors", color: "#C084FC" },
    { key: "payouts_pending", label: "Payouts pending", count: buckets.payouts_pending || 0, to: "/admin/payouts", color: "#EBB84C" },
    { key: "admin_inmail_unread", label: "Admin In-Mail", count: buckets.admin_inmail_unread || 0, to: "/dashboard/admin-inmail", color: "#F0C464" },
    { key: "support_messages_7d", label: "Support (7d)", count: buckets.support_messages_7d || 0, to: "/dashboard/support", color: "#60A5FA" },
    { key: "failed_logins_24h", label: "Failed logins (24h)", count: buckets.failed_logins_24h || 0, to: "/dashboard/admin", color: "#D14A85" },
    { key: "leak_detector", label: "Leak Detector", count: null, to: "/admin/leak-detector", color: "#F87171" },
  ];

  return (
    <div className="card p-6 border-2 border-[#EC008C]/30 bg-gradient-to-br from-[#0A0E18] to-[#050810]" data-testid="admin-inbox-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/40 flex items-center justify-center">
            <Bell className="w-6 h-6 text-[#EC008C]" />
          </div>
          <div>
            <h2 className="font-['Unbounded'] text-lg sm:text-xl font-bold text-white">Admin Inbox</h2>
            <p className="text-xs text-[#8A92A0]">All emails, messages and inquiries funnel here</p>
          </div>
        </div>
        <Link
          to="/admin/awaiting-review"
          className="text-right group hover:scale-105 transition-transform"
          data-testid="admin-inbox-total"
          title="View all items awaiting your review"
        >
          <p
            className="font-['Unbounded'] text-4xl sm:text-5xl font-bold text-[#EC008C] leading-none group-hover:text-[#FF3399]"
            data-testid="admin-inbox-total-count"
          >
            {summary.total_unread || 0}
          </p>
          <p className="text-xs text-[#8A92A0] mt-1 uppercase tracking-wider group-hover:text-white">Awaiting review →</p>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {cards.map((c) => (
          <Link
            key={c.key}
            to={c.to}
            className="p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#EC008C]/60 transition-colors flex items-center justify-between"
            data-testid={`admin-inbox-bucket-${c.key}`}
          >
            <span className="text-xs text-[#8A92A0]">{c.label}</span>
            <span
              className="text-base font-bold"
              style={{ color: c.count > 0 ? c.color : "#4A5262" }}
              data-testid={`admin-inbox-bucket-${c.key}-count`}
            >
              {c.count}
            </span>
          </Link>
        ))}
      </div>

      {/* Compliance & directory quick-links */}
      <div className="mt-4 pt-4 border-t border-[#1A2030] grid grid-cols-3 gap-2" data-testid="admin-inbox-directories">
        <Link
          to="/admin/buyers"
          className="p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#F0C464]/40 flex flex-col items-start"
          data-testid="admin-directory-buyers"
        >
          <span className="text-[10px] uppercase tracking-widest text-[#F0C464] font-bold">Directory</span>
          <span className="text-white font-bold text-sm">Buyers / Fans</span>
        </Link>
        <Link
          to="/admin/artists"
          className="p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#F0C464]/40 flex flex-col items-start"
          data-testid="admin-directory-artists"
        >
          <span className="text-[10px] uppercase tracking-widest text-[#F0C464] font-bold">Directory</span>
          <span className="text-white font-bold text-sm">Merchants / Artists</span>
        </Link>
        <Link
          to="/admin/tax-buckets"
          className="p-3 rounded-lg bg-[#050810] border border-[#1A2030] hover:border-[#F0C464]/40 flex flex-col items-start"
          data-testid="admin-directory-tax"
        >
          <span className="text-[10px] uppercase tracking-widest text-[#F0C464] font-bold">Tax</span>
          <span className="text-white font-bold text-sm">Quarterly Buckets</span>
        </Link>
      </div>
    </div>
  );
};

const LIVE_STORE_STAGES = {
  not_started: {
    label: "Get your Live Store badge",
    caption: "Set up your payout account so we can pay you directly.",
    cta: "Start payout setup",
    to: "/dashboard/payouts",
    percent: 0,
    accent: "#32D583",
  },
  identity_pending: {
    label: "Finish your identity check",
    caption: "We still need your ID and SSN/SIN. Takes ~3 minutes.",
    cta: "Continue payout setup",
    to: "/dashboard/payouts",
    percent: 33,
    accent: "#60A5FA",
  },
  under_review: {
    label: "We're reviewing your info",
    caption: "Usually clears in a few minutes. No action needed from you.",
    cta: "Check status",
    to: "/dashboard/payouts",
    percent: 66,
    accent: "#A78BFA",
  },
  bank_needed: {
    label: "Add your bank details",
    caption: "One last step — enter your bank so we can send your payouts.",
    cta: "Add bank details",
    to: "/dashboard/payouts",
    percent: 85,
    accent: "#32D583",
  },
};

const LiveStoreBadgeNudge = ({ stats }) => {
  const stage = stats?.stripe_stage;
  if (!stage || stage === "live") return null;
  const cfg = LIVE_STORE_STAGES[stage];
  if (!cfg) return null;

  return (
    <div
      className="card p-5 border border-[#8DB87A]/30 bg-gradient-to-r from-[#0A0E18] via-[#0A0E18] to-[#8DB87A]/[0.06]"
      data-testid="live-store-nudge"
      data-stage={stage}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          <div
            className="relative w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${cfg.accent}22`, border: `1px solid ${cfg.accent}66` }}
          >
            <span className="relative flex h-2 w-2">
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                style={{ background: cfg.accent }}
              ></span>
              <span
                className="relative inline-flex rounded-full h-2 w-2"
                style={{ background: cfg.accent }}
              ></span>
            </span>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#8DB87A] font-black">Live Store · {cfg.percent}%</p>
            <p
              className="font-['Unbounded'] text-base sm:text-lg font-bold text-white mt-0.5"
              data-testid="live-store-nudge-label"
            >
              {cfg.label}
            </p>
          </div>
        </div>
        <Link
          to={cfg.to}
          className="hidden sm:inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold text-black bg-[#EC008C] hover:bg-[#FF3399] transition-colors flex-shrink-0"
          data-testid="live-store-nudge-cta"
        >
          {cfg.cta} →
        </Link>
      </div>
      <p className="text-sm text-[#8A92A0] mb-3">{cfg.caption}</p>
      {/* Progress bar */}
      <div className="w-full h-1.5 bg-[#050810] rounded-full overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${cfg.percent}%`, background: `linear-gradient(90deg, ${cfg.accent}, #8DB87A)` }}
        />
      </div>
      <Link
        to={cfg.to}
        className="sm:hidden mt-3 inline-flex items-center justify-center gap-1 w-full py-2 rounded-lg text-sm font-semibold text-black bg-[#EC008C] hover:bg-[#FF3399] transition-colors"
        data-testid="live-store-nudge-cta-mobile"
      >
        {cfg.cta} →
      </Link>
    </div>
  );
};

const OnboardingChecklist = ({ stats, artistProfile }) => {
  const payoutDone = !!stats?.stripe_connected;
  const trackDone = (stats?.total_content || 0) + (stats?.total_products || 0) > 0;
  const saleDone = (stats?.total_orders_received || 0) > 0;

  const steps = [
    {
      key: "signup",
      label: "Create your account",
      caption: artistProfile ? `Welcome, ${artistProfile.stage_name}` : "You're in.",
      done: true,
      to: null,
      cta: null,
    },
    {
      key: "payout",
      label: "Connect Payout Account",
      caption: payoutDone ? "Payouts enabled" : "Payouts hit your linked bank on day 11",
      done: payoutDone,
      to: "/dashboard/payouts",
      cta: "Connect",
    },
    {
      key: "track",
      label: "Upload your first track",
      caption: trackDone
        ? `${(stats?.total_content || 0) + (stats?.total_products || 0)} uploaded`
        : "We auto-clip a 30-sec preview",
      done: trackDone,
      to: "/dashboard/content",
      cta: "Upload",
    },
    {
      key: "sale",
      label: "Make your first sale",
      caption: saleDone
        ? `${stats?.total_orders_received || 0} order${(stats?.total_orders_received || 0) === 1 ? "" : "s"} in`
        : "Share your product link to earn",
      done: saleDone,
      to: "/dashboard/products",
      cta: "Add product",
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const percent = Math.round((completedCount / steps.length) * 100);
  const isComplete = completedCount === steps.length;

  if (isComplete) {
    return (
      <div
        className="card p-6 border border-[#8DB87A]/40 bg-gradient-to-br from-[#0A0E18] to-[#050810]"
        data-testid="onboarding-checklist-complete"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#8DB87A]/15 border border-[#8DB87A]/40 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-[#8DB87A]" />
          </div>
          <div>
            <h2 className="font-['Unbounded'] text-lg font-bold text-white">
              You&apos;re fully set up
            </h2>
            <p className="text-xs text-[#8A92A0]">
              Payouts connected · Tracks live · First sale in the books
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="card p-6 border border-[#EC008C]/30 bg-gradient-to-br from-[#0A0E18] to-[#050810]"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-['Unbounded'] text-lg sm:text-xl font-bold text-white">
            Finish setting up your artist page
          </h2>
          <p className="text-xs text-[#8A92A0] mt-1">
            {completedCount} of {steps.length} steps complete
          </p>
        </div>
        <div className="text-right">
          <p
            className="font-['Unbounded'] text-3xl sm:text-4xl font-bold text-[#EC008C] leading-none"
            data-testid="onboarding-percent"
          >
            {percent}%
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-[#050810] rounded-full mb-6 overflow-hidden" data-testid="onboarding-progress-bar">
        <div
          className="h-full bg-gradient-to-r from-[#EC008C] to-[#FF6BC5] transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* 4-step grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((step, idx) => (
          <div
            key={step.key}
            className={`p-4 rounded-lg border transition-colors ${
              step.done
                ? "border-[#8DB87A]/40 bg-[#0A0E18]"
                : "border-[#1A2030] bg-[#050810]"
            }`}
            data-testid={`onboarding-step-${step.key}`}
          >
            <div className="flex items-start gap-3 mb-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.done ? "bg-[#8DB87A]/15" : "bg-[#1A2030]"
                }`}
              >
                {step.done ? (
                  <CheckCircle2 className="w-4 h-4 text-[#8DB87A]" />
                ) : (
                  <span className="text-xs text-[#8A92A0] font-semibold">{idx + 1}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className={`text-sm font-semibold truncate ${
                    step.done ? "text-[#8DB87A]" : "text-white"
                  }`}
                  data-testid={`onboarding-step-${step.key}-label`}
                >
                  {step.label}
                </p>
                <p className="text-xs text-[#8A92A0] mt-0.5">{step.caption}</p>
              </div>
            </div>
            {!step.done && step.to && (
              <Link
                to={step.to}
                className="inline-block mt-2 text-xs font-semibold text-[#EC008C] hover:underline"
                data-testid={`onboarding-step-${step.key}-cta`}
              >
                {step.cta} →
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const DashboardQuickActions = ({ showStoreHeading = false }) => (
  <section className="space-y-4" data-testid="dashboard-quick-actions">
    {showStoreHeading && (
      <h2 className="font-['Unbounded'] text-lg font-semibold text-white" data-testid="artist-store-heading">
        Create Your Own Store and Be Your Own Boss!
      </h2>
    )}
    <div className="card p-6">
      <h3 className="font-['Unbounded'] text-lg font-semibold text-white mb-4" data-testid="quick-actions-heading">Quick Actions</h3>
      <div className={`grid grid-cols-2 ${showStoreHeading ? "md:grid-cols-3 xl:grid-cols-6" : "md:grid-cols-6"} gap-4`}>
        <Link
          to="/dashboard/content"
          className="p-4 rounded-lg bg-[#0A0E18] border border-[#1A2030] hover:border-[#EC008C]/50 transition-colors text-center"
          data-testid="quick-action-upload"
        >
          <Upload className="w-6 h-6 text-[#EC008C] mx-auto mb-2" />
          <span className="text-sm text-white">Upload Content</span>
        </Link>
        <Link
          to="/dashboard/events"
          className="p-4 rounded-lg bg-[#0A0E18] border border-[#1A2030] hover:border-[#FF6BC5]/50 transition-colors text-center"
          data-testid="quick-action-event"
        >
          <Calendar className="w-6 h-6 text-[#FF6BC5] mx-auto mb-2" />
          <span className="text-sm text-white">Create Event</span>
        </Link>
        <Link
          to="/dashboard/products"
          className="p-4 rounded-lg bg-[#0A0E18] border border-[#1A2030] hover:border-[#D14A85]/50 transition-colors text-center"
          data-testid="quick-action-product"
        >
          <ShoppingBag className="w-6 h-6 text-[#D14A85] mx-auto mb-2" />
          <span className="text-sm text-white">Add Product</span>
        </Link>
        <Link
          to="/seller/orders"
          className="p-4 rounded-lg bg-[#0A0E18] border border-[#1A2030] hover:border-[#EC008C]/50 transition-colors text-center"
          data-testid="quick-action-shipping"
        >
          <Package className="w-6 h-6 text-[#EC008C] mx-auto mb-2" />
          <span className="text-sm text-white">Shipping</span>
        </Link>
        <Link
          to="/dashboard/subscriptions"
          className="p-4 rounded-lg bg-[#0A0E18] border border-[#1A2030] hover:border-[#D14A85]/50 transition-colors text-center"
          data-testid="quick-action-subscriptions"
        >
          <Heart className="w-6 h-6 text-[#D14A85] mx-auto mb-2" />
          <span className="text-sm text-white">Manage Subs</span>
        </Link>
        <Link
          to="/dashboard/profile"
          className="p-4 rounded-lg bg-[#0A0E18] border border-[#1A2030] hover:border-[#EC008C]/50 transition-colors text-center"
          data-testid="quick-action-profile"
        >
          <User className="w-6 h-6 text-[#EC008C] mx-auto mb-2" />
          <span className="text-sm text-white">Edit Profile</span>
        </Link>
      </div>
    </div>
  </section>
);

const DashboardOverview = ({ stats, artistProfile, isAdmin, token }) => {
  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      {isAdmin && <AdminInboxWidget token={token} />}
      {!isAdmin && <AdminMessageCallout />}
      {!isAdmin && artistProfile && (
        <LiveStoreBadgeNudge stats={stats} />
      )}
      {!isAdmin && artistProfile && (
        <OnboardingChecklist stats={stats} artistProfile={artistProfile} />
      )}
      <div>
        <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">
          Welcome back{artistProfile ? `, ${artistProfile.stage_name}` : ''}!
        </h1>
        <p className="text-[#8A92A0]">Here's what's happening with your profile.</p>
      </div>

      {!isAdmin && <DashboardQuickActions showStoreHeading />}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#EC008C]/10 flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-[#EC008C]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{stats.total_plays || 0}</p>
          <p className="text-sm text-[#8A92A0]">Total Plays</p>
        </div>

        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF6BC5]/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#FF6BC5]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{stats.upcoming_events || 0}</p>
          <p className="text-sm text-[#8A92A0]">Upcoming Events</p>
        </div>

        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#D14A85]/10 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-[#D14A85]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{stats.total_tickets_sold || 0}</p>
          <p className="text-sm text-[#8A92A0]">Tickets Sold</p>
        </div>

        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#EC008C]/10 flex items-center justify-center">
              <Music className="w-5 h-5 text-[#EC008C]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{stats.total_content || 0}</p>
          <p className="text-sm text-[#8A92A0]">Uploads</p>
        </div>

        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#D14A85]/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#D14A85]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{stats.total_subscribers || 0}</p>
          <p className="text-sm text-[#8A92A0]">Subscribers</p>
        </div>

        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#8DB87A]/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#8DB87A]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">${stats.monthly_subscription_revenue || 0}</p>
          <p className="text-sm text-[#8A92A0]">Monthly Subs</p>
        </div>
      </div>

      {isAdmin && <DashboardQuickActions />}
    </div>
  );
};

// Content Management Component
const ContentManagement = ({ token }) => {
  const editImage = useImageEditor();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [teaserItem, setTeaserItem] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content_type: "music",
    is_for_sale: false,
    price: "",
    file: null,
    poster: null
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (e) {
      console.error("Failed to fetch content:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm("Delete this content? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Content deleted");
      fetchContent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete content");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.file || !formData.title) {
      toast.error("Title and file are both required");
      return;
    }
    // File-size sanity check (100MB — content endpoint permits larger media than community feed)
    if (formData.file.size > 100 * 1024 * 1024) {
      toast.error(`File too large — ${(formData.file.size / 1024 / 1024).toFixed(1)}MB. The limit is 100MB.`);
      return;
    }
    // For-sale validation — price required if flagged for sale.
    const priceNum = parseFloat(formData.price);
    if (formData.is_for_sale && (!formData.price || isNaN(priceNum) || priceNum <= 0)) {
      toast.error("Enter a price > 0 Tokens to sell this content, or turn off 'For Sale' for a free stream.");
      return;
    }

    // Client-side 60s minimum for FOR-SALE audio/video. Saves
    // a huge upload roundtrip when the artist picks an obviously
    // out-of-band file. Server-side ffprobe still guards for spoofing.
    if (formData.is_for_sale && (formData.content_type === "music" || formData.content_type === "video")) {
      try {
        const durSecs = await new Promise((resolve, reject) => {
          const url = URL.createObjectURL(formData.file);
          const el = formData.content_type === "music" ? new Audio() : document.createElement("video");
          el.preload = "metadata";
          el.src = url;
          const t = setTimeout(() => { URL.revokeObjectURL(url); reject(new Error("probe_timeout")); }, 10000);
          el.onloadedmetadata = () => {
            clearTimeout(t);
            const d = el.duration;
            URL.revokeObjectURL(url);
            resolve(d);
          };
          el.onerror = () => {
            clearTimeout(t);
            URL.revokeObjectURL(url);
            reject(new Error("probe_error"));
          };
        });
        if (durSecs && isFinite(durSecs)) {
          if (durSecs < 60) {
            toast.error(`Media for sale must be at least 60 seconds. Yours is ${durSecs.toFixed(1)}s — upload the full-length file.`);
            return;
          }
        }
      } catch {
        // If the browser can't decode the metadata, defer to server-side ffprobe.
      }
    }

    setUploading(true);
    const toastId = toast.loading(`Uploading ${(formData.file.size / 1024 / 1024).toFixed(1)}MB… this can take a moment for large files. A 60-sec teaser is auto-generated after upload.`);
    try {
      const form = new FormData();
      form.append("file", formData.file);
      if (formData.poster) {
        form.append("poster", formData.poster);
      }

      const qs = new URLSearchParams({
        title: formData.title,
        description: formData.description || "",
        content_type: formData.content_type,
        is_for_sale: formData.is_for_sale ? "true" : "false",
        price: formData.is_for_sale ? String(priceNum) : "0",
        currency: "usd",
      }).toString();

      await axios.post(
        `${API}/content/upload?${qs}`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      toast.success(
        formData.is_for_sale
          ? `Content uploaded — for sale at ${priceNum} Tokens · 60-sec teaser generating…`
          : "Content uploaded successfully! · 60-sec teaser generating…",
        { id: toastId }
      );
      setDialogOpen(false);
      setFormData({ title: "", description: "", content_type: "music", is_for_sale: false, price: "", file: null, poster: null });
      fetchContent();
    } catch (err) {
      console.error("Upload failed:", err);
      const raw = err.response?.data?.detail;
      // Surface friendly copy for the media-duration guard.
      let message = raw || err.message || "Failed to upload content";
      if (raw && typeof raw === "object") {
        if (raw.code === "media_too_short" || raw.code === "media_too_long") {
          message = raw.message || `Media must be at least 60 seconds (observed ${raw.observed_secs}s).`;
        } else if (raw.message) {
          message = raw.message;
        }
      }
      toast.error(message, { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="content-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Content</h1>
          <p className="text-[#8A92A0]">Upload and manage your music and videos.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="upload-content-btn">
              <Plus className="w-4 h-4 mr-2" /> Upload
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030]">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-white">Upload Content</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label className="text-white">Title *</Label>
                <Input 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="Enter title"
                  data-testid="content-title-input"
                />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="Describe your content"
                  data-testid="content-description-input"
                />
              </div>
              <div>
                <Label className="text-white">Type</Label>
                <Select 
                  value={formData.content_type} 
                  onValueChange={(v) => setFormData({...formData, content_type: v})}
                >
                  <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="content-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                    <SelectItem value="music">Music</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="livestream">Livestream</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white">File *</Label>
                <Input 
                  type="file"
                  accept={formData.content_type === "music" ? "audio/*" : "video/*,audio/*"}
                  onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                  className="bg-[#050810] border-[#1A2030] text-white file:bg-[#1A2030] file:text-white file:border-0"
                  data-testid="content-file-input"
                />
                <p className="text-xs text-[#8A92A0] mt-1">A 60-second teaser is auto-generated for previews (audio or video).</p>
              </div>
              <div>
                <Label className="text-white flex items-center gap-2">
                  Movie Poster <span className="text-[10px] font-normal text-[#4A5260]">(optional — recommended for videos)</span>
                </Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={async (e) => {
                    const input = e.target;
                    if (!input.files?.[0]) return;
                    const image = await editImage(input.files[0], { maxBytes: 12 * 1024 * 1024 });
                    if (image) setFormData((prev) => ({ ...prev, poster: image }));
                    else input.value = "";
                  }}
                  className="bg-[#050810] border-[#1A2030] text-white file:bg-[#1A2030] file:text-white file:border-0"
                  data-testid="content-poster-input"
                />
                <p className="text-xs text-[#8A92A0] mt-1">Attach a hero image so the storefront card looks like a real release, not a raw filename.</p>
                {formData.poster && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-[#8DB87A]">
                    <img
                      src={URL.createObjectURL(formData.poster)}
                      alt="poster preview"
                      className="w-16 h-16 rounded object-contain bg-black border border-[#1A2030]"
                    />
                    <span>{formData.poster.name}</span>
                  </div>
                )}
              </div>
              <div className="p-3 rounded-lg bg-[#050810] border border-[#1A2030]">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_for_sale}
                    onChange={(e) => setFormData({...formData, is_for_sale: e.target.checked})}
                    className="w-4 h-4 accent-[#EC008C]"
                    data-testid="content-for-sale-toggle"
                  />
                  <div>
                    <div className="text-sm text-white font-semibold">Sell this content</div>
                    <div className="text-xs text-[#8A92A0]">Fans only stream the full file after purchase. The 60-sec teaser stays public.</div>
                  </div>
                </label>
                {formData.is_for_sale && (
                  <>
                    <div className="mt-3 flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="bg-[#050810] border-[#1A2030] text-white"
                        placeholder="5"
                        data-testid="content-price-input"
                      />
                      <span className="text-xs text-[#8A92A0]">Tokens · 88/12 split (90/10 Founders)</span>
                    </div>
                    <div className="mt-2 rounded-md border border-[#EC008C]/30 bg-[#EC008C]/[0.05] px-3 py-2 text-xs text-[#EC008C] font-semibold" data-testid="content-duration-hint">
                      Media for sale must be at least 60 seconds. Full-length files are welcome.
                    </div>
                  </>
                )}
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                disabled={uploading}
                data-testid="submit-upload-btn"
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : content.length === 0 ? (
        <div className="card p-12 text-center">
          <Music className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <p className="text-[#8A92A0]">No content uploaded yet. Start sharing your work!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {content.map((item) => (
            <div key={item.content_id} className="card p-4" data-testid={`content-item-${item.content_id}`}>
              <div className="aspect-video rounded-lg bg-[#1A2030] mb-3 flex items-center justify-center overflow-hidden relative">
                {item.poster_url ? (
                  <img
                    src={item.poster_url.startsWith("http") ? item.poster_url : `${process.env.REACT_APP_BACKEND_URL}${item.poster_url}`}
                    alt={item.title}
                    className="w-full h-full object-contain bg-black"
                  />
                ) : item.content_type === "music" ? (
                  <Music className="w-8 h-8 text-[#EC008C]" />
                ) : (
                  <PlayCircle className="w-8 h-8 text-[#FF6BC5]" />
                )}
                {item.preview_url && (
                  <button
                    onClick={() => setTeaserItem(item)}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity"
                    aria-label="Play 60-second teaser"
                    data-testid={`play-teaser-${item.content_id}`}
                  >
                    <span className="w-12 h-12 rounded-full bg-[#EC008C] text-black flex items-center justify-center shadow-lg">
                      <PlayCircle className="w-6 h-6" />
                    </span>
                  </button>
                )}
              </div>
              <h3 className="font-semibold text-white mb-1">{item.title}</h3>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <p className="text-sm text-[#8A92A0] capitalize">{item.content_type}</p>
                {item.is_for_sale && item.price > 0 ? (
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#EC008C]/15 text-[#EC008C]">
                    For sale · ${Number(item.price).toFixed(2)}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#1A2030] text-[#8A92A0]">
                    Free stream
                  </span>
                )}
                {item.preview_url && (
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#8DB87A]/15 text-[#8DB87A]" title="60-sec public preview clipped by ffmpeg">
                    60s teaser ready
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-[#8A92A0]">
                  <PlayCircle className="w-3 h-3" />
                  <span>{item.plays} plays</span>
                </div>
                <button
                  onClick={() => handleDelete(item.content_id)}
                  className="text-[#8A92A0] hover:text-[#D14A85] transition-colors p-1"
                  title="Delete content"
                  data-testid={`delete-content-${item.content_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TeaserModal
        open={!!teaserItem}
        onClose={() => setTeaserItem(null)}
        item={teaserItem ? {
          title: teaserItem.title,
          previewUrl: teaserItem.preview_url,
          posterUrl: teaserItem.poster_url,
          contentType: teaserItem.content_type,
          productId: teaserItem.linked_product_id,
          priceLabel: teaserItem.is_for_sale && teaserItem.price > 0 ? `$${Number(teaserItem.price).toFixed(2)}` : null,
        } : null}
        purchaseState={{ owned: true, downloadsUsed: 0, downloadsRemaining: 3, maxDownloads: 3 }}
        token={token}
      />
    </div>
  );
};

// Events Management Component
const EventsManagement = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    venue: "",
    location: "",
    event_date: "",
    event_time: "",
    ticket_price: "",
    tickets_available: "",
    payment_link: ""
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (e) {
      console.error("Failed to fetch events:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, {
        ...formData,
        ticket_price: parseFloat(formData.ticket_price),
        tickets_available: parseInt(formData.tickets_available),
        payment_link: formData.payment_link || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event created successfully!");
      setDialogOpen(false);
      setFormData({
        title: "", description: "", venue: "", location: "",
        event_date: "", event_time: "", ticket_price: "", tickets_available: "", payment_link: ""
      });
      fetchEvents();
    } catch (e) {
      console.error("Create event failed:", e);
      if (e.response?.status === 403) {
        toast.error("Create your Artist Profile first under the Profile tab");
      } else {
        toast.error(e.response?.data?.detail || "Failed to create event");
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="events-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Events</h1>
          <p className="text-[#8A92A0]">Create and manage your concerts and shows.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-white">Create Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="text-white">Event Title *</Label>
                <Input 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="e.g., Summer Tour 2026"
                  required
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="Describe your event"
                  data-testid="event-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Venue *</Label>
                  <Input 
                    value={formData.venue}
                    onChange={(e) => setFormData({...formData, venue: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="Venue name"
                    required
                    data-testid="event-venue-input"
                  />
                </div>
                <div>
                  <Label className="text-white">Location *</Label>
                  <Input 
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="City, Country"
                    required
                    data-testid="event-location-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Date *</Label>
                  <Input 
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    required
                    data-testid="event-date-input"
                  />
                </div>
                <div>
                  <Label className="text-white">Time *</Label>
                  <Input 
                    type="time"
                    value={formData.event_time}
                    onChange={(e) => setFormData({...formData, event_time: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    required
                    data-testid="event-time-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Ticket Price (Tokens) *</Label>
                  <Input 
                    type="number"
                    step="1"
                    min="0"
                    value={formData.ticket_price}
                    onChange={(e) => setFormData({...formData, ticket_price: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="50"
                    required
                    data-testid="event-price-input"
                  />
                  <p className="text-xs text-[#8A92A0] mt-1">1 Token = $1 USD. Fans pay in Tokens.</p>
                </div>
                <div>
                  <Label className="text-white">Tickets Available *</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={formData.tickets_available}
                    onChange={(e) => setFormData({...formData, tickets_available: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="100"
                    required
                    data-testid="event-tickets-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                data-testid="submit-event-btn"
              >
                Create Event
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="card p-12 text-center">
          <Calendar className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <p className="text-[#8A92A0]">No events yet. Create your first event!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map((event) => (
            <div key={event.event_id} className="card p-6" data-testid={`event-item-${event.event_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-white text-lg">{event.title}</h3>
                  <p className="text-sm text-[#8A92A0]">{event.venue} • {event.location}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  event.status === "upcoming" ? "bg-[#EC008C]/10 text-[#EC008C]" :
                  event.status === "completed" ? "bg-[#A1A1AA]/10 text-[#8A92A0]" :
                  "bg-[#D14A85]/10 text-[#D14A85]"
                }`}>
                  {event.status}
                </span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <p className="text-[#8A92A0]">Date</p>
                  <p className="text-white">{event.event_date}</p>
                </div>
                <div>
                  <p className="text-[#8A92A0]">Price</p>
                  <p className="text-[#EC008C]">{event.ticket_price} Tokens</p>
                </div>
                <div>
                  <p className="text-[#8A92A0]">Sold</p>
                  <p className="text-white">{event.tickets_sold}/{event.tickets_available}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Products Management Component
const ProductsManagement = ({ token }) => {
  const editImage = useImageEditor();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [digitalFile, setDigitalFile] = useState(null);
  // Digital Focus split: "digital" is the default flow. Physical merchandise
  // (Flow B) only appears if the admin has enabled physical_merchandise_enabled.
  const [physicalEnabled, setPhysicalEnabled] = useState(true);
  // Flow selector — "digital" (Flow A) or "physical" (Flow B). Drives which
  // categories the Category dropdown offers.
  const [productFlow, setProductFlow] = useState("digital");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "digital",
    stock: "",
    payment_link: "",
    requires_shipping: false,
    livestream_url: "",
    livestream_starts_at: "",
    challenge_brief: "",
    challenge_deadline: "",
    subcategory: "",
    attributes: {},
  });

  // Fetch the public platform-settings once on mount so we know whether to
  // show the Physical flow at all. If the endpoint fails we default to
  // showing everything (fail-open — better than hiding an artist's ability
  // to sell just because a request flaked).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/platform-settings/public`);
        if (!cancelled) setPhysicalEnabled(!!r.data?.physical_merchandise_enabled);
      } catch (_e) {
        if (!cancelled) setPhysicalEnabled(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchProducts();
    // Auto-open the "Add Product" dialog if the URL requests it (from the
    // post-payout-onboarding CTA on the Payouts page → /dashboard/products?add=1)
    const params = new URLSearchParams(window.location.search);
    if (params.get("add") === "1") {
      // Default to "digital" so the artist lands directly on the track-upload flow
      setFormData((prev) => ({ ...prev, category: "digital" }));
      setDialogOpen(true);
      window.history.replaceState({}, "", "/dashboard/products");
    }
    // Fresh from Payout Account onboarding → drop the artist directly
    // into "Add your first product" with a celebration toast.
    if (params.get("onboarding") === "complete") {
      toast.success("Payouts connected — your store is LIVE. Add your first product.", { duration: 6000 });
      setFormData((prev) => ({ ...prev, category: "digital" }));
      setDialogOpen(true);
      window.history.replaceState({}, "", "/dashboard/products");
    }
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (e) {
      console.error("Failed to fetch products:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    // Require a digital file for digital/album products so the buyer flow works
    if ((formData.category === "digital" || formData.category === "album") && !digitalFile) {
      toast.error("Attach the full track file (MP3/WAV/ZIP) so buyers can download after purchase.");
      return;
    }
    const creating = toast.loading("Creating product…");
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        payment_link: formData.payment_link || null,
        requires_shipping: !!formData.requires_shipping,
        livestream_url: formData.livestream_url || null,
        livestream_starts_at: formData.livestream_starts_at || null,
        challenge_brief: formData.challenge_brief || null,
        challenge_deadline: formData.challenge_deadline || null,
        subcategory: formData.subcategory || null,
        attributes: formData.attributes || {},
      };
      const res = await axios.post(`${API}/products`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // If digital category and a file is selected, upload it now
      if ((formData.category === "digital" || formData.category === "album") && digitalFile && res.data?.product_id) {
        toast.loading(`Uploading track (${(digitalFile.size / 1024 / 1024).toFixed(1)}MB)…`, { id: creating });
        const fd = new FormData();
        fd.append("file", digitalFile);
        await axios.post(`${API}/products/${res.data.product_id}/digital-file`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
      }
      toast.success("Product created — ready to sell!", { id: creating });
      setDialogOpen(false);
      setFormData({ name: "", description: "", price: "", category: "digital", stock: "", payment_link: "", requires_shipping: false, livestream_url: "", livestream_starts_at: "", challenge_brief: "", challenge_deadline: "", subcategory: "", attributes: {} });
      setProductFlow("digital");
      setDigitalFile(null);
      fetchProducts();
    } catch (err) {
      console.error("Create product failed:", err);
      if (err.response?.status === 403) {
        toast.error("Create your Artist Profile first under the Profile tab", { id: creating });
      } else {
        toast.error(err.response?.data?.detail || "Failed to create product", { id: creating });
      }
    }
  };

  const handleMediaUpload = async (productId, file) => {
    if (file.size > 100 * 1024 * 1024) {
      toast.error(`Media too large — ${(file.size / 1024 / 1024).toFixed(1)}MB. Max is 100MB.`);
      return;
    }
    file = await editImage(file, { maxBytes: 100 * 1024 * 1024 });
    if (!file) return;
    setUploading(true);
    const t = toast.loading(`Uploading ${(file.size / 1024 / 1024).toFixed(1)}MB…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/products/${productId}/media`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success(`${res.data.kind === "videos" ? "Video" : "Image"} uploaded!`, { id: t });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed", { id: t });
    } finally {
      setUploading(false);
    }
  };

  // Remove a single image or video from a product. Wires to the existing
  // DELETE /api/products/{productId}/media?url=... route and refreshes the
  // product list so the thumbnail disappears immediately.
  const handleMediaRemove = async (productId, url) => {
    if (!confirm("Remove this media from the product? This cannot be undone.")) return;
    const t = toast.loading("Removing…");
    try {
      await axios.delete(`${API}/products/${productId}/media`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { url },
      });
      toast.success("Removed", { id: t });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed", { id: t });
    }
  };

  // Delete an entire product. Calls DELETE /api/products/{productId} which
  // verifies ownership server-side, then refetches the list. Compliance
  // records (past orders / payouts) are preserved by the backend.
  const handleProductDelete = async (product) => {
    const label = product?.name || "this product";
    if (!confirm(`Delete "${label}"? This removes it from your store. Past orders and payout records stay intact for compliance.`)) return;
    const t = toast.loading("Deleting…");
    try {
      await axios.delete(`${API}/products/${product.product_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`"${label}" deleted`, { id: t });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed", { id: t });
    }
  };

  return (
    <div className="space-y-6" data-testid="products-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Products</h1>
          <p className="text-[#8A92A0]">Sell merchandise and digital products.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-white">Add Product</DialogTitle>
              <DialogDescription data-testid="add-product-description" className="text-[#8A92A0]">Choose a digital item, digital collectible, or physical product.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="text-white">Product Name *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="e.g., Tour T-Shirt"
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="Describe your product"
                  data-testid="product-description-input"
                />
              </div>
              {/* Flow Chooser — Digital Focus split.
                * Flow A (Digital): tracks, albums, livestream passes, remix challenge entries — pure digital / no shipping.
                * Flow B (Physical): merchandise + physical editions — requires shipping.
                * When physicalEnabled === false (admin toggle off), Flow B is hidden entirely and the artist is locked into Flow A. */}
              {physicalEnabled && (
                <div data-testid="product-flow-chooser">
                  <Label className="text-white">What are you selling?</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setProductFlow("digital");
                        setFormData((prev) => ({ ...prev, category: "digital", requires_shipping: false }));
                      }}
                      className={`rounded-lg border p-3 text-left transition ${productFlow === "digital" ? "border-[#EC008C] bg-[#EC008C]/10" : "border-[#1A2030] bg-[#050810] hover:border-[#EC008C]/40"}`}
                      data-testid="flow-a-digital-btn"
                    >
                      <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Flow A</p>
                      <p className="text-white text-sm font-semibold">Digital</p>
                      <p className="text-[11px] text-[#8A92A0]">Tracks · Albums · Livestreams · Challenges</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setProductFlow("physical");
                        setFormData((prev) => ({ ...prev, category: "merch", requires_shipping: true }));
                      }}
                      className={`rounded-lg border p-3 text-left transition ${productFlow === "physical" ? "border-[#EC008C] bg-[#EC008C]/10" : "border-[#1A2030] bg-[#050810] hover:border-[#EC008C]/40"}`}
                      data-testid="flow-b-physical-btn"
                    >
                      <p className="text-[10px] uppercase tracking-widest text-[#F0C464] font-bold">Flow B</p>
                      <p className="text-white text-sm font-semibold">Physical Merch</p>
                      <p className="text-[11px] text-[#8A92A0]">Hoodie · T-shirt · Vinyl</p>
                    </button>
                  </div>
                </div>
              )}
              {!physicalEnabled && (
                <div className="rounded-lg border border-[#1A2030] bg-[#050810] p-3" data-testid="digital-only-banner">
                  <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold mb-1">Digital Only</p>
                  <p className="text-xs text-[#8A92A0]">Physical merchandise is currently disabled by the platform. You&apos;re selling a digital product — instant download, no shipping needed.</p>
                </div>
              )}

              <Link to="/dashboard/collectibles" onClick={() => setDialogOpen(false)} data-testid="choose-digital-collectible" className="flex items-center gap-2 text-sm text-[#FF66BB] hover:text-white transition-colors">
                <Sparkles className="w-4 h-4" />Digital collectible · JPG / PNG →
              </Link>
              <div>
                <Label className="text-white">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v, requires_shipping: ["merch","collectible"].includes(v) })}
                >
                  <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                    {productFlow === "digital" && (
                      <>
                        <SelectItem value="digital">Digital download (Single, Demo Pack)</SelectItem>
                        <SelectItem value="album">Album (digital)</SelectItem>
                        <SelectItem value="livestream">Livestream pass (Live Studio Session)</SelectItem>
                        <SelectItem value="challenge">Remix Challenge Entry</SelectItem>
                      </>
                    )}
                    {productFlow === "physical" && physicalEnabled && (
                      <>
                        <SelectItem value="merch">Merchandise (physical — Hoodie, T-shirt)</SelectItem>
                        <SelectItem value="collectible">Physical edition (Vinyl, etc.)</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Universal marketplace-style basic details — ALWAYS visible for physical categories */}
              {(formData.category === "merch" || formData.category === "collectible" || formData.category === "experience") && (
                <BasicProductDetails
                  attributes={formData.attributes || {}}
                  onAttributesChange={(a) => setFormData({ ...formData, attributes: a })}
                />
              )}

              {/* Structured physical-goods subcategory + attributes (clothing, shoes, food, vehicles…) */}
              {(formData.category === "merch" || formData.category === "collectible") && (
                <div className="border-t border-[#1A2030] pt-4">
                  <ProductSubcategoryFields
                    subcategory={formData.subcategory}
                    attributes={formData.attributes}
                    onSubcategoryChange={(v) => setFormData({ ...formData, subcategory: v })}
                    onAttributesChange={(a) => setFormData({ ...formData, attributes: a })}
                  />
                </div>
              )}

              {/* Shipping section — surfaces the fields the buyer will fill at checkout */}
              {(formData.category === "merch" || formData.category === "collectible") && (
                <div className="border-t border-[#1A2030] pt-4 space-y-3" data-testid="shipping-section">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Shipping</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="requires-shipping-toggle">
                    <input
                      type="checkbox"
                      checked={!!formData.requires_shipping}
                      onChange={(e) => setFormData({ ...formData, requires_shipping: e.target.checked })}
                      className="w-4 h-4 accent-[#EC008C]"
                    />
                    <span className="text-sm text-white">This product needs to be shipped to the buyer</span>
                  </label>
                  {formData.requires_shipping && (
                    <div className="grid sm:grid-cols-2 gap-3 pl-6">
                      <div>
                        <Label className="text-white text-sm">Ships from</Label>
                        <Input
                          value={formData.attributes?.ships_from || ""}
                          onChange={(e) => setFormData({ ...formData, attributes: { ...formData.attributes, ships_from: e.target.value } })}
                          placeholder="e.g. Berlin, Germany"
                          className="bg-[#050810] border-[#1A2030] text-white"
                          data-testid="ships-from-input"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-sm">Ships worldwide?</Label>
                        <Select
                          value={formData.attributes?.ships_worldwide ? "yes" : "no"}
                          onValueChange={(v) => setFormData({ ...formData, attributes: { ...formData.attributes, ships_worldwide: v === "yes" } })}
                        >
                          <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="ships-worldwide-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                            <SelectItem value="yes">Yes — worldwide shipping</SelectItem>
                            <SelectItem value="no">No — domestic only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-white text-sm">Handling time (days)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="60"
                          value={formData.attributes?.handling_days || ""}
                          onChange={(e) => setFormData({ ...formData, attributes: { ...formData.attributes, handling_days: e.target.value } })}
                          placeholder="e.g. 2"
                          className="bg-[#050810] border-[#1A2030] text-white"
                          data-testid="handling-days-input"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-sm">Est. delivery time</Label>
                        <Input
                          value={formData.attributes?.delivery_estimate || ""}
                          onChange={(e) => setFormData({ ...formData, attributes: { ...formData.attributes, delivery_estimate: e.target.value } })}
                          placeholder="e.g. 5-10 business days"
                          className="bg-[#050810] border-[#1A2030] text-white"
                          data-testid="delivery-estimate-input"
                        />
                      </div>
                    </div>
                  )}
                  {!formData.requires_shipping && (
                    <p className="text-xs text-[#6A7280]">
                      Leave unchecked for digital-only products (delivered via instant download / calendar invite).
                    </p>
                  )}
                </div>
              )}

              {/* Digital file upload — only for digital/album (music tracks, demo packs, albums) */}
              {(formData.category === "digital" || formData.category === "album") && (
                <>
                  <div className="border-t border-[#1A2030] pt-4 space-y-3" data-testid="track-metadata-section">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">
                        {formData.category === "album" ? "Album Details" : "Track Details"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white text-sm">Genre</Label>
                        <Select
                          value={formData.attributes?.genre || ""}
                          onValueChange={(v) => setFormData({ ...formData, attributes: { ...formData.attributes, genre: v } })}
                        >
                          <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="track-genre-select">
                            <SelectValue placeholder="Select genre" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                            {["Pop","Rock","Hip-Hop","R&B","Electronic","House","Techno","Trap","Lo-fi","Jazz","Classical","Country","Latin","K-Pop","J-Pop","Indie","Metal","Folk","Reggae","Afrobeat","Other"].map(g => (
                              <SelectItem key={g} value={g}>{g}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-white text-sm">BPM</Label>
                        <Input
                          type="number"
                          min="40"
                          max="300"
                          value={formData.attributes?.bpm || ""}
                          onChange={(e) => setFormData({ ...formData, attributes: { ...formData.attributes, bpm: e.target.value } })}
                          className="bg-[#050810] border-[#1A2030] text-white"
                          placeholder="e.g. 128"
                          data-testid="track-bpm-input"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-sm">Key</Label>
                        <Input
                          value={formData.attributes?.musical_key || ""}
                          onChange={(e) => setFormData({ ...formData, attributes: { ...formData.attributes, musical_key: e.target.value } })}
                          className="bg-[#050810] border-[#1A2030] text-white"
                          placeholder="e.g. C minor"
                          data-testid="track-key-input"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-sm">Duration</Label>
                        <Input
                          value={formData.attributes?.duration || ""}
                          onChange={(e) => setFormData({ ...formData, attributes: { ...formData.attributes, duration: e.target.value } })}
                          className="bg-[#050810] border-[#1A2030] text-white"
                          placeholder="e.g. 3:42"
                          data-testid="track-duration-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white text-sm">License / Usage rights</Label>
                      <Select
                        value={formData.attributes?.license || "personal"}
                        onValueChange={(v) => setFormData({ ...formData, attributes: { ...formData.attributes, license: v } })}
                      >
                        <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="track-license-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                          <SelectItem value="personal">Personal listening only</SelectItem>
                          <SelectItem value="content_creator">Content creator (YouTube, TikTok, streams)</SelectItem>
                          <SelectItem value="commercial">Commercial (ads, film, TV)</SelectItem>
                          <SelectItem value="extended">Extended / exclusive (custom terms)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white text-sm">Release date <span className="text-[#6A7280]">(optional)</span></Label>
                      <Input
                        type="date"
                        value={formData.attributes?.release_date || ""}
                        onChange={(e) => setFormData({ ...formData, attributes: { ...formData.attributes, release_date: e.target.value } })}
                        className="bg-[#050810] border-[#1A2030] text-white"
                        data-testid="track-release-date-input"
                      />
                    </div>
                  </div>

                  <div data-testid="digital-file-section" className="border-t border-[#1A2030] pt-4">
                    <div className="flex items-baseline justify-between mb-1">
                      <Label className="text-white">Full track file <span className="text-[#EC008C]">*</span></Label>
                      {digitalFile && <span className="text-xs text-[#00E68A]">{(digitalFile.size / 1024 / 1024).toFixed(1)}MB ready</span>}
                    </div>
                    <input
                      type="file"
                      accept="audio/*,application/zip,application/x-zip-compressed,.mp3,.wav,.flac,.m4a,.aac,.zip"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (f && f.size > 500 * 1024 * 1024) {
                          toast.error(`File too large — ${(f.size / 1024 / 1024).toFixed(1)}MB. Max is 500MB.`);
                          e.target.value = "";
                          return;
                        }
                        setDigitalFile(f);
                      }}
                      className="bg-[#050810] border border-[#1A2030] text-white text-sm w-full p-2 rounded"
                      data-testid="digital-file-input"
                    />
                    <p className="text-xs text-[#6A7280] mt-1.5">
                      MP3, WAV, FLAC, or ZIP. Buyers get an instant download link right after payment is confirmed.
                      You keep 90% of every sale.
                    </p>
                    <div className="mt-3 flex items-start gap-2.5 p-3 rounded-lg bg-[#00E68A]/[0.06] border border-[#00E68A]/25" data-testid="preview-auto-callout">
                      <span className="mt-0.5 text-[#00E68A] text-lg leading-none">🎧</span>
                      <div className="text-xs text-[#8A92A0] leading-relaxed">
                        <span className="text-[#00E68A] font-semibold">Auto-generated 60-second preview.</span> As soon as you upload,
                        StardomFans slices the first 60 seconds of your track into a public teaser buyers can taste-test on the product page.
                        <strong className="text-white"> The full file stays locked behind secure checkout</strong> — buyers only unlock it after paying.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Livestream details */}
              {formData.category === "livestream" && (
                <div className="space-y-2" data-testid="livestream-section">
                  <Label className="text-white">Livestream join URL</Label>
                  <Input
                    value={formData.livestream_url}
                    onChange={(e) => setFormData({...formData, livestream_url: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="https://zoom.us/j/... or your YouTube live URL"
                    data-testid="livestream-url-input"
                  />
                  <Label className="text-white">Starts at (ISO datetime)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.livestream_starts_at}
                    onChange={(e) => setFormData({...formData, livestream_starts_at: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    data-testid="livestream-starts-input"
                  />
                </div>
              )}

              {/* Challenge details */}
              {formData.category === "challenge" && (
                <div className="space-y-2" data-testid="challenge-section">
                  <Label className="text-white">Challenge brief</Label>
                  <Textarea
                    value={formData.challenge_brief}
                    onChange={(e) => setFormData({...formData, challenge_brief: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="Remix the stems we provide and upload your version"
                    data-testid="challenge-brief-input"
                  />
                  <Label className="text-white">Submission deadline</Label>
                  <Input
                    type="datetime-local"
                    value={formData.challenge_deadline}
                    onChange={(e) => setFormData({...formData, challenge_deadline: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    data-testid="challenge-deadline-input"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Price (Tokens) *</Label>
                  <Input 
                    type="number"
                    step="1"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="30"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <Label className="text-white">Stock *</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData({...formData, stock: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="50"
                    required
                    data-testid="product-stock-input"
                  />
                </div>
              </div>
              <p className="text-xs text-[#6A7280]">You can add photos/videos after creating the product.</p>
              <div className="rounded-lg border border-[#00E68A]/25 bg-[#00E68A]/[0.05] p-4">
                <p className="text-xs text-white font-semibold mb-1">Payments &amp; payouts</p>
                <p className="text-xs text-[#8A92A0] leading-relaxed">
                  Checkout is wired to your <span className="text-[#00E68A] font-semibold">Stripe Connect account</span>.
                  Sales settle to your bank automatically minus the 12% platform fee (10% for Founders Circle) and Stripe processing fees — no payment links to manage.
                </p>
                <p className="text-xs text-[#6A7280] mt-2">
                  Haven&apos;t connected yet?{" "}
                  <Link to="/dashboard/payouts" className="text-[#00E68A] font-semibold hover:underline" data-testid="connect-payout-account-cta">
                    Connect your Payout Account →
                  </Link>
                </p>
              </div>

              {/* Transparent fee breakdown so artists always know what lands in their account */}
              <div className="rounded-xl border border-[#00E68A]/25 bg-[#00E68A]/[0.05] p-4 space-y-2" data-testid="fee-transparency">
                <p className="text-[10px] uppercase tracking-widest text-[#00E68A] font-bold">Transparent 88/12 revenue split (90/10 for Founders Circle)</p>
                <p className="text-xs text-[#8A92A0] leading-relaxed">
                  After the refund window closes, revenue is auto-split every sale:
                </p>
                <table className="w-full text-xs mt-1">
                  <tbody>
                    <tr className="border-b border-[#00E68A]/15"><td className="text-[#8A92A0] py-1.5">Stripe processing fee</td><td className="text-right text-white py-1.5">2.9% + $0.30</td></tr>
                    <tr className="border-b border-[#00E68A]/15"><td className="text-[#8A92A0] py-1.5">Stripe payout fee</td><td className="text-right text-white py-1.5">~0.25% + $0.25</td></tr>
                    <tr className="border-b border-[#00E68A]/15"><td className="text-[#8A92A0] py-1.5">StardomFans platform fee</td><td className="text-right text-white py-1.5">12% (10% Founders)</td></tr>
                    <tr><td className="text-[#00E68A] font-semibold py-1.5">You receive (net wire)</td><td className="text-right text-[#00E68A] font-bold py-1.5">~84.85% − ~$0.55</td></tr>
                  </tbody>
                </table>
                <p className="text-[10px] text-[#6A7280] mt-1 leading-relaxed">
                  Fans top up their wallet fee-free (1 Token = $1). Sales tax is added at product checkout only — never at top-up — and routed to the tax vault. Your Day-11 wire is the matured pool minus Stripe fees.
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                data-testid="submit-product-btn"
              >
                Add Product
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="card p-12 text-center">
          <ShoppingBag className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <p className="text-[#8A92A0]">No products yet. Start selling!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <div key={product.product_id} className="card p-4" data-testid={`product-item-${product.product_id}`}>
              <div className="aspect-square rounded-lg bg-[#1A2030] mb-3 flex items-center justify-center overflow-hidden relative group">
                {product.images && product.images.length > 0 ? (
                  <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain bg-black" />
                ) : (
                  <ShoppingBag className="w-8 h-8 text-[#D14A85]" />
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <div className="text-center">
                    <Upload className="w-6 h-6 text-white mx-auto mb-1" />
                    <span className="text-xs text-white">{uploading ? "Uploading..." : "Add Photo/Video"}</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={async (e) => {
                      const input = e.target;
                      if (input.files[0]) await handleMediaUpload(product.product_id, input.files[0]);
                      input.value = "";
                    }}
                    data-testid={`upload-media-${product.product_id}`}
                  />
                </label>
              </div>
              {/* Media thumbnails strip — every image + video with an X to remove it. */}
              {(((product.images || []).length + (product.videos || []).length) > 0) && (
                <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2" data-testid={`media-strip-${product.product_id}`}>
                  {(product.images || []).map((url, idx) => (
                    <div key={`img-${idx}`} className="relative flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-[#1A2030] group/thumb">
                      <img src={url} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-contain" />
                      <button
                        type="button"
                        onClick={() => handleMediaRemove(product.product_id, url)}
                        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/80 hover:bg-[#EC008C] flex items-center justify-center opacity-70 group-hover/thumb:opacity-100 transition"
                        title="Remove image"
                        aria-label={`Remove image ${idx + 1}`}
                        data-testid={`remove-image-${product.product_id}-${idx}`}
                      >
                        <X className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  ))}
                  {(product.videos || []).map((url, idx) => (
                    <div key={`vid-${idx}`} className="relative flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-[#1A2030] flex items-center justify-center group/thumb">
                      <PlayCircle className="w-5 h-5 text-white/80" />
                      <button
                        type="button"
                        onClick={() => handleMediaRemove(product.product_id, url)}
                        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/80 hover:bg-[#EC008C] flex items-center justify-center opacity-70 group-hover/thumb:opacity-100 transition"
                        title="Remove video"
                        aria-label={`Remove video ${idx + 1}`}
                        data-testid={`remove-video-${product.product_id}-${idx}`}
                      >
                        <X className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <h3 className="font-semibold text-white mb-1">{product.name}</h3>
              <p className="text-sm text-[#8A92A0] mb-2">{product.category}</p>
              <div className="flex items-center justify-between">
                <span className="text-[#EC008C] font-semibold">${product.price}</span>
                <span className="text-xs text-[#8A92A0]">{product.stock} in stock</span>
              </div>
              <button
                type="button"
                onClick={() => handleProductDelete(product)}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-[#EC008C]/30 bg-[#EC008C]/5 text-[#EC008C] text-xs font-semibold hover:bg-[#EC008C]/10 hover:border-[#EC008C]/60 transition"
                data-testid={`delete-product-${product.product_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Product
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-[#1A2030] pt-8 mt-8"><DigitalCollectiblesManagement token={token} compact /></div>
    </div>
  );
};

// Profile Management Component
const ProfileManagement = ({ token, artistProfile, onUpdate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    stage_name: artistProfile?.stage_name || "",
    bio: artistProfile?.bio || "",
    genre: artistProfile?.genre || "",
    location: artistProfile?.location || ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (artistProfile) {
        // Update existing profile
        await axios.put(`${API}/artists/me`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Profile updated!");
      } else {
        // Create new artist profile
        await axios.post(`${API}/artists/profile`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Artist profile created!");
      }
      onUpdate();
    } catch (e) {
      console.error("Profile update failed:", e);
      toast.error(e.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-management">
      <div>
        <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">
          {artistProfile ? "Edit Profile" : "Create Artist Profile"}
        </h1>
        <p className="text-[#8A92A0]">
          {artistProfile ? "Update your artist information." : "Set up your artist profile to start creating."}
        </p>
      </div>

      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-[#1A2030] flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-[#8A92A0]" />
              )}
            </div>
            <div>
              <p className="text-white font-medium">{user?.name}</p>
              <p className="text-sm text-[#8A92A0]">{user?.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-white">Stage Name *</Label>
              <Input 
                value={formData.stage_name}
                onChange={(e) => setFormData({...formData, stage_name: e.target.value})}
                className="bg-[#050810] border-[#1A2030] text-white"
                placeholder="Your artist name"
                required
                data-testid="profile-stage-name-input"
              />
            </div>
            <div>
              <Label className="text-white">Genre</Label>
              <Input 
                value={formData.genre}
                onChange={(e) => setFormData({...formData, genre: e.target.value})}
                className="bg-[#050810] border-[#1A2030] text-white"
                placeholder="e.g., Hip Hop, Electronic"
                data-testid="profile-genre-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-white">Location</Label>
            <Input 
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="bg-[#050810] border-[#1A2030] text-white"
              placeholder="City, Country"
              data-testid="profile-location-input"
            />
          </div>

          <div>
            <Label className="text-white">Bio</Label>
            <Textarea 
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              className="bg-[#050810] border-[#1A2030] text-white min-h-[150px]"
              placeholder="Tell fans about yourself..."
              data-testid="profile-bio-input"
            />
          </div>

          <Button 
            type="submit" 
            className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
            disabled={loading}
            data-testid="save-profile-btn"
          >
            {loading ? "Saving..." : artistProfile ? "Save Changes" : "Create Profile"}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Subscriptions Management Component
const SubscriptionsManagement = ({ token }) => {
  const [tiers, setTiers] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    tier_level: "premium",
    benefits: "",
    payment_link: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, supportersRes] = await Promise.all([
        axios.get(`${API}/subscriptions/tiers/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscriptions/supporters`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTiers(tiersRes.data);
      setSupporters(supportersRes.data);
    } catch (e) {
      console.error("Failed to fetch subscription data:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const benefits = formData.benefits.split('\n').filter(b => b.trim());
      await axios.post(`${API}/subscriptions/tiers`, {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        tier_level: formData.tier_level,
        benefits: benefits,
        payment_link: formData.payment_link || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription tier created!");
      setDialogOpen(false);
      setFormData({ name: "", description: "", price: "", tier_level: "premium", benefits: "", payment_link: "" });
      fetchData();
    } catch (e) {
      console.error("Create tier failed:", e);
      if (e.response?.status === 403) {
        toast.error("Create your Artist Profile first under the Profile tab");
      } else {
        toast.error(e.response?.data?.detail || "Failed to create tier");
      }
    }
  };

  const handleDelete = async (tierId) => {
    if (!confirm("Are you sure you want to deactivate this tier?")) return;
    try {
      await axios.delete(`${API}/subscriptions/tiers/${tierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tier deactivated");
      fetchData();
    } catch (e) {
      console.error("Delete tier failed:", e);
      toast.error("Failed to deactivate tier");
    }
  };

  const totalMonthlyRevenue = supporters.reduce((sum, sub) => {
    return sum + (sub.tier?.price || 0);
  }, 0);

  return (
    <div className="space-y-6" data-testid="subscriptions-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Supporter Subscriptions</h1>
          <p className="text-[#8A92A0]">Create subscription tiers for supporters to support you monthly.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="create-tier-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Tier
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030]">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-white">Create Subscription Tier</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="text-white">Tier Name *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="e.g., Supporter, VIP, Superfan"
                  required
                  data-testid="tier-name-input"
                />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="Describe what supporters get"
                  data-testid="tier-description-input"
                />
              </div>
              <div>
                <Label className="text-white">Monthly Price (Tokens) *</Label>
                <Input 
                  type="number"
                  step="1"
                  min="1"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="10"
                  required
                  data-testid="tier-price-input"
                />
              </div>
              <div>
                <Label className="text-white">Tier Level *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[
                    { value: "free", label: "Free", desc: "Basic access", color: "#A1A1AA" },
                    { value: "premium", label: "Premium", desc: "$5-$15/mo", color: "#C084FC" }
                  ].map(level => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData({...formData, tier_level: level.value})}
                      data-testid={`tier-level-${level.value}`}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.tier_level === level.value
                          ? `border-[${level.color}] bg-[${level.color}]/10`
                          : "border-[#1A2030] bg-[#050810] hover:border-[#52525B]"
                      }`}
                    >
                      <p className="font-semibold text-sm" style={{ color: formData.tier_level === level.value ? level.color : "#fff" }}>{level.label}</p>
                      <p className="text-[#6A7280] text-xs">{level.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-white">Benefits (one per line)</Label>
                <Textarea 
                  value={formData.benefits}
                  onChange={(e) => setFormData({...formData, benefits: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="Early access to new music&#10;Exclusive behind-the-scenes&#10;Monthly Q&A sessions"
                  rows={4}
                  data-testid="tier-benefits-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                data-testid="submit-tier-btn"
              >
                Create Tier
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#D14A85]/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#D14A85]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{supporters.length}</p>
          <p className="text-sm text-[#8A92A0]">Active Subscribers</p>
        </div>
        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#8DB87A]/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#8DB87A]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">${totalMonthlyRevenue.toFixed(2)}</p>
          <p className="text-sm text-[#8A92A0]">Monthly Revenue</p>
        </div>
        <div className="stat-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#EC008C]/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#EC008C]" />
            </div>
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{tiers.length}</p>
          <p className="text-sm text-[#8A92A0]">Active Tiers</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          {/* Subscription Tiers */}
          <div className="card p-6">
            <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">Your Tiers</h2>
            {tiers.length === 0 ? (
              <div className="text-center py-8">
                <Heart className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                <p className="text-[#8A92A0]">No subscription tiers yet. Create one to let fans support you!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tiers.map((tier) => (
                  <div key={tier.tier_id} className="bg-[#050810] rounded-lg p-6 border border-[#1A2030]" data-testid={`tier-${tier.tier_id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-white text-lg">{tier.name}</h3>
                        <p className="text-2xl font-['Unbounded'] font-bold text-[#EC008C]">
                          ${tier.price}<span className="text-sm text-[#8A92A0] font-normal">/mo</span>
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(tier.tier_id)}
                        className="text-[#8A92A0] hover:text-[#D14A85] hover:bg-[#D14A85]/10"
                        data-testid={`delete-tier-${tier.tier_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {tier.description && (
                      <p className="text-[#8A92A0] text-sm mb-4">{tier.description}</p>
                    )}
                    {tier.benefits && tier.benefits.length > 0 && (
                      <ul className="space-y-2">
                        {tier.benefits.map((benefit, idx) => (
                          <li key={idx} className="text-sm text-[#8A92A0] flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#EC008C]" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Supporters List */}
          <div className="card p-6">
            <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">Your Supporters</h2>
            {supporters.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                <p className="text-[#8A92A0]">No subscribers yet. Share your subscription tiers with fans!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {supporters.map((sub) => (
                  <div key={sub.subscription_id} className="flex items-center gap-4 bg-[#050810] rounded-lg p-4 border border-[#1A2030]">
                    <div className="w-10 h-10 rounded-full bg-[#1A2030] flex items-center justify-center overflow-hidden">
                      {sub.fan?.picture ? (
                        <img src={sub.fan.picture} alt={sub.fan.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-[#8A92A0]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{sub.fan?.name || "Anonymous"}</p>
                      <p className="text-sm text-[#8A92A0]">{sub.tier?.name || "Unknown tier"} • ${sub.tier?.price || 0}/mo</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#8A92A0]">Since {new Date(sub.started_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Analytics: imported from components/AnalyticsDashboard.jsx (see top of file)

// (Old inline analytics block removed — replaced by imported AnalyticsDashboard component)

// Manual payout request — artist submits an amount; enters the admin approval
// queue (see /admin/payouts). Amount goes into signed credit ledger on approval.
const ManualPayoutRequestCard = ({ token }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [balance, setBalance] = useState(null);

  const fetchMine = async () => {
    try {
      const [bRes, hRes] = await Promise.all([
        axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/credits/history?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setBalance(bRes.data);
      setMine((hRes.data.rows || []).filter(r => r.reason === "payout_approved" || r.reason === "payout_requested"));
    } catch { /* non-fatal */ }
  };

  useEffect(() => { fetchMine(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (!n || n <= 0) return toast.error("Enter an amount > $0");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/payouts/request`,
        { amount_cents: Math.round(n * 100), note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payout request submitted — the treasury team will review shortly.");
      setAmount("");
      setNote("");
      fetchMine();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit payout request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card p-5 sm:p-6" data-testid="manual-payout-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold mb-1">Treasury</p>
          <h3 className="font-['Unbounded'] text-lg font-bold text-white">Request Manual Payout</h3>
          <p className="text-xs text-[#8A92A0] mt-1">Submit an amount; the platform admin approves it in the payout console.</p>
        </div>
        {balance && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Token balance</p>
            <p className="text-xl font-bold text-white">{balance.balance_display}</p>
          </div>
        )}
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 items-stretch">
        <div className="flex items-center gap-2 bg-[#050810] border border-[#1A2030] rounded-full px-3 flex-1">
          <span className="text-[#8A92A0]">$</span>
          <input
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent outline-none text-white py-2"
            placeholder="Amount USD"
            data-testid="payout-amount-input"
          />
        </div>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="bg-[#050810] border border-[#1A2030] rounded-full px-4 py-2 text-white outline-none flex-1"
          placeholder="Optional note (period, urgency, etc.)"
          data-testid="payout-note-input"
        />
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-[#EC008C] text-black font-bold px-5 py-2 hover:bg-[#FF3399] disabled:opacity-60"
          data-testid="payout-submit-btn"
        >
          {submitting ? "Submitting…" : "Request payout"}
        </button>
      </form>
      {mine.length > 0 && (
        <ul className="mt-4 space-y-1" data-testid="payout-history-list">
          {mine.slice(0, 5).map((r) => (
            <li key={r.entry_id} className="flex items-center justify-between text-xs text-[#8A92A0]">
              <span className="font-mono">{r.created_at?.slice(0, 10)} · {r.reason.replace("_", " ")}</span>
              <span className={r.delta < 0 ? "text-[#EC008C]" : "text-[#8DB87A]"}>
                {r.delta < 0 ? "-" : "+"}${Math.abs(r.delta / 100).toFixed(2)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Payouts Management Component — pure wallet flow.
// StardomFans Ltd. maintains a signed Token ledger for every artist. On the
// artist's chosen cadence (Day-11 default / biweekly / monthly), the AI
// treasury loop fires a real `stripe.Transfer.create` to the artist's
// connected Stripe Express account for their share (88% Standard / 90%
// Founders), netting Stripe processing + payout fees off the artist's cut
// so the platform's 12% (or 10%) stays whole. Idempotency keys make it
// physically impossible to send twice.
const StripeConnectCard = ({ stripeAccount, connectCountry, setConnectCountry, connectLoading, onConnect }) => {
  const connected = Boolean(stripeAccount?.connected);
  const chargesOK = Boolean(stripeAccount?.charges_enabled);
  const payoutsOK = Boolean(stripeAccount?.payouts_enabled);
  const fullyReady = connected && chargesOK && payoutsOK;
  return (
    <div
      className={`rounded-2xl border p-6 ${fullyReady ? "border-[#8DB87A]/40 bg-[#8DB87A]/[0.05]" : "border-[#EC008C]/40 bg-[#EC008C]/[0.04]"}`}
      data-testid="stripe-connect-card"
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${fullyReady ? "bg-[#8DB87A]/20 text-[#8DB87A]" : "bg-[#EC008C]/20 text-[#EC008C]"}`}>
          {fullyReady ? <ShieldCheck className="w-6 h-6" /> : <ShieldAlert className="w-6 h-6" />}
        </div>
        <div className="flex-1">
          <h2 className="font-['Unbounded'] text-lg font-bold mb-1 text-white">
            {fullyReady ? "Stripe Connect · Live" : connected ? "Finish Stripe onboarding" : "Connect with Stripe"}
          </h2>
          {fullyReady && (
            <p className="text-[#8A92A0] text-sm">
              Your account is verified. Sales settle to your Stripe balance and payouts release on your chosen cadence.
            </p>
          )}
          {connected && !fullyReady && (
            <p className="text-[#8A92A0] text-sm">
              Stripe still needs a few details from you before payouts unlock. Continue onboarding to finish up.
            </p>
          )}
          {!connected && (
            <p className="text-[#8A92A0] text-sm">
              Every artist plugs into <span className="text-white">Stripe Connect Express</span> for payouts. You must complete onboarding before you can publish products, events or paid content.
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#0A0E18] border border-[#1A2030] text-xs text-white">
              {connected ? <CheckCircle2 className="w-3 h-3 text-[#8DB87A]" /> : <div className="w-3 h-3 rounded-full border border-[#4A5260]" />} Account
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#0A0E18] border border-[#1A2030] text-xs text-white">
              {chargesOK ? <CheckCircle2 className="w-3 h-3 text-[#8DB87A]" /> : <div className="w-3 h-3 rounded-full border border-[#4A5260]" />} Charges enabled
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#0A0E18] border border-[#1A2030] text-xs text-white">
              {payoutsOK ? <CheckCircle2 className="w-3 h-3 text-[#8DB87A]" /> : <div className="w-3 h-3 rounded-full border border-[#4A5260]" />} Payouts enabled
            </span>
          </div>

          {!fullyReady && (
            <div className="mt-5 flex flex-wrap items-end gap-3">
              {!connected && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8A92A0]">Business country</label>
                  <select
                    value={connectCountry}
                    onChange={(e) => setConnectCountry(e.target.value)}
                    className="mt-1 h-10 rounded-lg bg-[#0A0E18] border border-[#1A2030] text-white px-3 text-sm"
                    data-testid="stripe-country-select"
                  >
                    {["CA","US","GB","AU","NZ","IE","FR","DE","ES","IT","NL","SE","JP","SG","HK","MX","IN"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}
              <Button
                onClick={onConnect}
                disabled={connectLoading}
                className="bg-[#EC008C] text-black font-bold hover:bg-[#FF3399] h-10 px-6"
                data-testid="stripe-connect-btn"
              >
                {connectLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                {connected ? "Continue Stripe onboarding" : "Connect with Stripe"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PAYOUT_CADENCES = [
  { id: "day11", label: "Day 11 (default)", body: "Every matured sale wires on the eleventh day. Fastest cash-flow." },
  { id: "biweekly", label: "Biweekly", body: "Wires roll up every 14 days once matured. Fewer transfers on your statement." },
  { id: "monthly", label: "Monthly", body: "Wires land on the 1st of each UTC month. Cleanest for bookkeeping." },
];

const PayoutCadenceCard = ({ cadence, saving, onPick }) => (
  <div className="rounded-2xl border border-[#1A2030] bg-[#0A0E18] p-6" data-testid="cadence-card">
    <div className="flex items-center gap-3 mb-4">
      <CalendarDays className="w-5 h-5 text-[#EC008C]" />
      <h2 className="font-['Unbounded'] text-lg font-bold text-white">Payout cadence</h2>
    </div>
    <p className="text-[#8A92A0] text-sm mb-5">
      Choose how often matured sales release to your Stripe balance. Change any time.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {PAYOUT_CADENCES.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onPick(c.id)}
          disabled={saving || cadence === c.id}
          className={`text-left rounded-xl border p-4 transition-colors ${cadence === c.id ? "border-[#EC008C] bg-[#EC008C]/[0.06]" : "border-[#1A2030] hover:border-[#EC008C]/50 bg-[#050810]"}`}
          data-testid={`cadence-option-${c.id}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-white">{c.label}</span>
            {cadence === c.id && <CheckCircle2 className="w-4 h-4 text-[#EC008C]" />}
          </div>
          <p className="text-xs text-[#8A92A0] leading-relaxed">{c.body}</p>
        </button>
      ))}
    </div>
  </div>
);


const PayoutsManagement = ({ token }) => {
  const [wallet, setWallet] = useState(null);
  const [split, setSplit] = useState(null);
  const [bank, setBank] = useState(null);
  const [bankDraft, setBankDraft] = useState({});
  const [savingBank, setSavingBank] = useState(false);
  const [channelsDraft, setChannelsDraft] = useState({
    interac_email: "",
    paypal_handle: "",
    stripe_link: "",
    notes: "",
  });
  const [savingChannels, setSavingChannels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justOnboarded, setJustOnboarded] = useState(false);
  // Stripe Connect Express state
  const [stripeAccount, setStripeAccount] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectCountry, setConnectCountry] = useState("CA");
  const [payoutCadence, setPayoutCadence] = useState("day11");
  const [savingCadence, setSavingCadence] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    const params = new URLSearchParams(window.location.search);
    if (params.get('onboarding') === 'complete') {
      toast.success("Stripe onboarding submitted — payouts unlock as soon as Stripe verifies you.");
      setJustOnboarded(true);
      window.history.replaceState({}, '', '/dashboard/payouts');
    } else if (params.get('refresh') === 'true') {
      toast.info("Onboarding paused — click Connect with Stripe to pick up where you left off.");
      window.history.replaceState({}, '', '/dashboard/payouts');
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletRes, bankRes, channelsRes, splitRes, stripeRes] = await Promise.all([
        axios.get(`${API}/payouts/wallet-summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/payouts/artist-bank`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/payouts/artist-channels`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { channels: {} } })),
        axios.get(`${API}/me/split`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/payouts/account`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { connected: false } })),
      ]);
      setWallet(walletRes.data);
      setSplit(splitRes.data);
      setBank(bankRes.data);
      setStripeAccount(stripeRes.data);
      setPayoutCadence(channelsRes.data?.payout_cadence || "day11");
      setBankDraft({
        beneficiary_name: bankRes.data?.beneficiary_name || "",
        bank_name: bankRes.data?.bank_name || "",
        bank_address: bankRes.data?.bank_address || "",
        country: bankRes.data?.country || "",
        currency: bankRes.data?.currency || "USD",
        transit: "",
        institution: "",
        account_number: "",
        swift: bankRes.data?.swift || "",
        iban: "",
      });
      const c = channelsRes.data?.channels || {};
      setChannelsDraft({
        interac_email: c.interac_email || "",
        paypal_handle: c.paypal_handle || "",
        stripe_link: c.stripe_link || "",
        notes: c.notes || "",
      });
    } catch (e) {
      console.error("Failed to fetch wallet payouts data:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      // Only send non-blank values so the artist can update partial fields
      // without wiping the ones we already have on file.
      const payload = Object.fromEntries(
        Object.entries(bankDraft).filter(([, v]) => (v || "").trim() !== "")
      );
      await axios.put(`${API}/payouts/artist-bank`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Bank details saved — day-11 auto-payout is armed");
      await fetchData();
    } catch (err) {
      const raw = err.response?.data?.detail;
      let msg = raw || "Save failed";
      if (raw && typeof raw === "object") {
        if (raw.code === "identity_anchor_mismatch") {
          msg = `Identity Anchor blocked this save.\n\nKYC name: ${raw.kyc_name || "(missing)"}\nYou entered: ${raw.bank_beneficiary || "(missing)"}\n\nThe beneficiary must match your KYC legal name. If you legally changed your name, re-upload a new ID in the ID Vault.`;
        } else if (raw.message) {
          msg = raw.message;
        }
      }
      toast.error(msg);
    } finally {
      setSavingBank(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  const bankConnected = bank?.connected;
  const matured = wallet?.matured_cents || 0;
  const pending = wallet?.pending_cents || 0;

  return (
    <div className="space-y-8" data-testid="payouts-management">
      {/* Manual payout request — flows into the admin-approval queue */}
      <ManualPayoutRequestCard token={token} />

      {/* Post-onboarding CTA — appears once the bank details are on file */}
      {(justOnboarded || bankConnected) && (
        <div className="rounded-2xl border-2 border-[#EC008C] bg-gradient-to-br from-[#EC008C]/15 via-[#0A0E18] to-transparent p-6 sm:p-8 relative overflow-hidden" data-testid="upload-track-cta">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#EC008C]/20 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[#EC008C] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#EC008C]/40">
              <Music className="w-8 h-8 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold mb-1">
                {justOnboarded ? "You're ready to sell — start earning right now" : "Ready to sell? Upload your first track"}
              </p>
              <h2 className="font-['Unbounded'] text-xl sm:text-2xl font-bold text-white mb-1 leading-tight">
                Upload your first track for sale
              </h2>
              <p className="text-sm text-[#8A92A0] leading-relaxed">
                We&apos;ll auto-clip a 60-second preview the moment you upload, so fans can taste-test before buying.
                Every purchase drops 90 Tokens per $100 into your wallet and adds one authentic vote toward the
                <Link to="/awards/2027" className="text-[#EC008C] font-semibold hover:underline"> 2027 Top Global Music Artist</Link> award.
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard/products?add=1")}
              className="bg-[#EC008C] text-black hover:bg-[#FF3399] font-bold rounded-full px-6 h-12 flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
              data-testid="upload-track-cta-btn"
            >
              <Upload className="w-4 h-4" /> Upload &amp; sell a track
            </button>
          </div>
        </div>
      )}

      <div>
        <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Wallet &amp; Payouts</h1>
        <p className="text-[#8A92A0]">Every sale is a Token credit on your wallet. On your chosen cadence (Day-11 / biweekly / monthly), a real Stripe Connect transfer fires to your bank at the split shown below.</p>
      </div>

      {/* --- Stripe Connect Express card --- */}
      <StripeConnectCard
        stripeAccount={stripeAccount}
        connectCountry={connectCountry}
        setConnectCountry={setConnectCountry}
        connectLoading={connectLoading}
        onConnect={async () => {
          setConnectLoading(true);
          try {
            const origin_url = window.location.origin;
            const { data } = await axios.post(
              `${API}/payouts/connect/onboard`,
              null,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: { origin_url, country: connectCountry },
              },
            );
            if (data?.onboarding_url) {
              window.location.href = data.onboarding_url;
            } else {
              toast.error("Stripe did not return an onboarding link. Try again.");
            }
          } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start Stripe onboarding");
          } finally {
            setConnectLoading(false);
          }
        }}
      />

      {/* --- Payout cadence picker --- */}
      <PayoutCadenceCard
        cadence={payoutCadence}
        saving={savingCadence}
        onPick={async (next) => {
          setSavingCadence(true);
          try {
            await axios.put(
              `${API}/payouts/cadence`,
              { cadence: next },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            setPayoutCadence(next);
            toast.success(`Payout cadence saved: ${next}`);
          } catch (e) {
            const detail = e.response?.data?.detail;
            if (e.response?.status === 423 || detail?.code === "kyc_required") {
              toast.error("Finish ID verification first — then pick a cadence.");
            } else {
              toast.error(detail?.message || detail || "Failed to save cadence");
            }
          } finally {
            setSavingCadence(false);
          }
        }}
      />

      {/* --- Split Preview — bank-grade transparency card --- */}
      {split && (
        <div
          className={`card p-6 relative overflow-hidden ${
            split.is_founders_circle
              ? "border-[#EC008C]/50 bg-gradient-to-br from-[#EC008C]/[0.08] to-transparent"
              : "border-[#1A2030] bg-[#0A0E18]"
          }`}
          data-testid="artist-split-preview"
        >
          {/* Silver bank-grade ribbon at the top */}
          <div
            aria-hidden="true"
            className="absolute top-0 left-0 right-0 h-[3px] sf-silver-plate sf-silver-shine-animate"
          />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="sf-silver-chip" data-testid="split-tier-chip">
                  {split.tier}
                </span>
                {split.is_founders_circle && (
                  <span className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">
                    · Grandfathered forever
                  </span>
                )}
              </div>
              <h3 className="font-['Unbounded'] text-2xl font-bold text-white" data-testid="split-headline">
                Your split · {split.artist_share_pct}
                <span className="text-[#8A92A0] mx-1">/</span>
                {split.platform_share_pct}
              </h3>
              <p className="text-xs text-[#8A92A0] mt-1">
                You keep <b className="text-white">{split.artist_share_pct}%</b>. StardomFans Ltd.
                takes <b className="text-white">{split.platform_share_pct}%</b> to power the fortress
                — no hidden fees, no drift, no surprises.
              </p>
            </div>
            <div className="rounded-xl border border-[#1A2030] bg-[#050810] p-3 min-w-[220px]" data-testid="split-example">
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0] font-bold mb-1">Example · $100 gross sale</p>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-white">You take home</span>
                <span className="font-mono text-lg font-bold text-[#8DB87A]">
                  ${(split.example.artist_take_cents / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-sm text-[#8A92A0]">Platform fee</span>
                <span className="font-mono text-sm text-[#8A92A0]">
                  ${(split.example.platform_fee_cents / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Token wallet balance */}
      <div className="card p-6" data-testid="artist-wallet-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-[#EC008C]/40 bg-gradient-to-br from-[#EC008C]/12 to-transparent p-5" data-testid="artist-total-balance">
            <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Total Token balance</p>
            <p className="text-3xl font-['Unbounded'] font-bold text-white mt-1">{wallet?.balance_display || "0 Tokens"}</p>
            <p className="text-xs text-[#8A92A0] mt-2">1 Token = $1 USD. Only USD is settled — currency conversion happens at payout time.</p>
          </div>
          <div className="rounded-xl border border-[#8DB87A]/40 bg-gradient-to-br from-[#8DB87A]/10 to-transparent p-5" data-testid="artist-matured-balance">
            <p className="text-[10px] uppercase tracking-widest text-[#8DB87A] font-bold">Matured (day 11+)</p>
            <p className="text-3xl font-['Unbounded'] font-bold text-[#8DB87A] mt-1">{Math.floor(matured / 100).toLocaleString()} Tokens</p>
            <p className="text-xs text-[#8A92A0] mt-2">Ready to wire on the next auto-payout run.</p>
          </div>
          <div className="rounded-xl border border-[#FF6BC5]/30 bg-gradient-to-br from-[#FF6BC5]/10 to-transparent p-5" data-testid="artist-pending-balance">
            <p className="text-[10px] uppercase tracking-widest text-[#FF6BC5] font-bold">On hold (day 1–10)</p>
            <p className="text-3xl font-['Unbounded'] font-bold text-[#FF6BC5] mt-1">{Math.floor(pending / 100).toLocaleString()} Tokens</p>
            <p className="text-xs text-[#8A92A0] mt-2">Refund window (7d) + 4d clearing. Matures automatically.</p>
          </div>
        </div>
        {wallet?.next_payout_eta && (
          <div className="mt-4 rounded-lg bg-[#050810] border border-[#1A2030] p-4 flex items-center justify-between gap-4" data-testid="next-payout-eta">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0] font-bold">Next auto-payout</p>
              <p className="text-sm text-white mt-0.5">
                <span className="font-mono">{new Date(wallet.next_payout_eta).toLocaleDateString()}</span>
                <span className="text-[#8A92A0]"> · {wallet.maturity_days}-day rolling window</span>
              </p>
            </div>
            <span className="text-xs text-[#8DB87A] font-semibold">AI-scheduled</span>
          </div>
        )}
      </div>

      {/* Bank details form */}
      <div className="card p-6" data-testid="artist-bank-card">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h2 className="font-['Unbounded'] text-xl font-semibold text-white flex items-center gap-2">
              <Banknote className="w-5 h-5 text-[#EC008C]" />
              Your Bank Account
            </h2>
            <p className="text-sm text-[#8A92A0] mt-1">Where StardomFans Ltd. wires your day-11 auto-payout. USD or your local currency.</p>
          </div>
          {bankConnected && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#8DB87A]/10 border border-[#8DB87A]/40 text-[#8DB87A] text-xs font-bold">
              <CheckCircle className="w-3.5 h-3.5" /> On file · ****{bank?.account_number?.slice(-4) || bank?.iban?.slice(-4) || "----"}
            </span>
          )}
        </div>
        <form onSubmit={saveBank} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="artist-bank-form">
          <BankField label="Beneficiary name" value={bankDraft.beneficiary_name} onChange={(v) => setBankDraft({ ...bankDraft, beneficiary_name: v })} placeholder="Legal name on the account" testid="ab-beneficiary" />
          <BankField label="Bank name" value={bankDraft.bank_name} onChange={(v) => setBankDraft({ ...bankDraft, bank_name: v })} placeholder="e.g. RBC Royal Bank" testid="ab-bank-name" />
          <BankField label="Bank address" value={bankDraft.bank_address} onChange={(v) => setBankDraft({ ...bankDraft, bank_address: v })} placeholder="Street, city, country" testid="ab-bank-address" full />
          <BankField label="Country (ISO)" value={bankDraft.country} onChange={(v) => setBankDraft({ ...bankDraft, country: v.toUpperCase() })} placeholder="CA · US · GB · ..." testid="ab-country" />
          <BankField label="Payout currency" value={bankDraft.currency} onChange={(v) => setBankDraft({ ...bankDraft, currency: v.toUpperCase() })} placeholder="USD" testid="ab-currency" />
          <BankField label="Transit / Sort code / Routing" value={bankDraft.transit} onChange={(v) => setBankDraft({ ...bankDraft, transit: v })} placeholder={bank?.transit ? `Currently on file: ${bank.transit}` : "e.g. 12345 (Canada) · 026009593 (US ABA)"} testid="ab-transit" />
          <BankField label="Institution number" value={bankDraft.institution} onChange={(v) => setBankDraft({ ...bankDraft, institution: v })} placeholder={bank?.institution ? `Currently on file: ${bank.institution}` : "Canada-only: 003 · 004 · 001 ..."} testid="ab-institution" />
          <BankField label="Account number" value={bankDraft.account_number} onChange={(v) => setBankDraft({ ...bankDraft, account_number: v })} placeholder={bank?.account_number ? `Currently on file: ${bank.account_number}` : "Full account number"} testid="ab-account-number" />
          <BankField label="SWIFT / BIC" value={bankDraft.swift} onChange={(v) => setBankDraft({ ...bankDraft, swift: v.toUpperCase() })} placeholder="8 or 11 characters" testid="ab-swift" />
          <BankField label="IBAN (if applicable)" value={bankDraft.iban} onChange={(v) => setBankDraft({ ...bankDraft, iban: v.toUpperCase() })} placeholder={bank?.iban ? `Currently on file: ${bank.iban}` : "e.g. GB29 NWBK ..."} testid="ab-iban" full />
          <div className="md:col-span-2 flex items-center justify-between border-t border-[#1A2030] pt-4 mt-2 gap-3 flex-wrap">
            <p className="text-xs text-[#6A7280]">Stored encrypted-at-rest. Every save is audit-logged. StardomFans Ltd. staff can never read your full account number.</p>
            <button
              type="submit"
              disabled={savingBank}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#EC008C] text-black font-bold hover:bg-[#FF3399] disabled:opacity-60"
              data-testid="artist-bank-save"
            >
              <ShieldCheck className="w-4 h-4" /> {savingBank ? "Saving..." : "Save bank details"}
            </button>
          </div>
        </form>
      </div>

      {/* --- Structured payout channels (Interac / PayPal / Stripe link) --- */}
      <div className="card p-6" data-testid="artist-channels-card">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-[#EC008C]" />
          <h2 className="font-['Unbounded'] text-xl font-semibold text-white">Instant Payout Links</h2>
        </div>
        <p className="text-xs text-[#8A92A0] mb-4">
          On payout day the admin sees these next to your name and pays you directly. Add
          whichever channels work for you — you can leave the ones you don&apos;t use blank.
        </p>
        <form
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setSavingChannels(true);
            try {
              const payload = Object.fromEntries(
                Object.entries(channelsDraft).filter(([, v]) => (v || "").trim() !== "")
              );
              await axios.put(`${API}/payouts/artist-channels`, payload, {
                headers: { Authorization: `Bearer ${token}` },
              });
              toast.success("Payout channels saved — admin can now pay you in one click");
              await fetchData();
            } catch (err) {
              const raw = err.response?.data?.detail;
              toast.error(typeof raw === "string" ? raw : raw?.message || "Save failed");
            } finally {
              setSavingChannels(false);
            }
          }}
          data-testid="artist-channels-form"
        >
          <BankField
            label="Interac e-Transfer email"
            value={channelsDraft.interac_email}
            onChange={(v) => setChannelsDraft({ ...channelsDraft, interac_email: v })}
            placeholder="you@example.com"
            testid="ac-interac"
          />
          <BankField
            label="PayPal handle / email"
            value={channelsDraft.paypal_handle}
            onChange={(v) => setChannelsDraft({ ...channelsDraft, paypal_handle: v })}
            placeholder="@yourhandle · or paypal email"
            testid="ac-paypal"
          />
          <BankField
            label="Stripe payment link"
            value={channelsDraft.stripe_link}
            onChange={(v) => setChannelsDraft({ ...channelsDraft, stripe_link: v })}
            placeholder="https://buy.stripe.com/..."
            testid="ac-stripe"
            full
          />
          <BankField
            label="Notes for the admin (optional)"
            value={channelsDraft.notes}
            onChange={(v) => setChannelsDraft({ ...channelsDraft, notes: v })}
            placeholder="e.g. Prefer Interac between 9–5 EST"
            testid="ac-notes"
            full
          />
          <div className="md:col-span-2 flex items-center justify-between pt-2 gap-3 flex-wrap">
            <p className="text-xs text-[#6A7280]">
              Never shown to fans. Only visible to admins during payout approval.
            </p>
            <button
              type="submit"
              disabled={savingChannels}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#EC008C] text-black font-bold hover:bg-[#FF3399] disabled:opacity-60"
              data-testid="artist-channels-save"
            >
              <ShieldCheck className="w-4 h-4" /> {savingChannels ? "Saving…" : "Save payout channels"}
            </button>
          </div>
        </form>
      </div>

      {/* Recent payouts */}
      {wallet?.recent_payouts && wallet.recent_payouts.length > 0 && (
        <div className="card p-6" data-testid="artist-recent-payouts">
          <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">Recent Wire Payouts</h2>
          <div className="space-y-3">
            {wallet.recent_payouts.map((p) => (
              <div key={p.entry_id} className="flex items-center justify-between p-4 bg-[#050810] rounded-lg border border-[#1A2030]">
                <div>
                  <p className="text-white font-medium">${Math.abs((p.delta || 0) / 100).toFixed(2)} <span className="text-xs text-[#8A92A0]">wire</span></p>
                  <p className="text-xs text-[#8A92A0]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""} · {p.memo || "day-11 auto-payout"}</p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#8DB87A]/10 text-[#8DB87A]">paid</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How Payouts Work */}
      <div className="card p-6">
        <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">How Payouts Work</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-3">
              <span className="text-[#EC008C] font-bold">1</span>
            </div>
            <h3 className="font-semibold text-white mb-1">Fans top up Tokens</h3>
            <p className="text-sm text-[#8A92A0]">Stripe Checkout — no fees, no tax at top-up. 1 Token = $1 USD.</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-3">
              <span className="text-[#EC008C] font-bold">2</span>
            </div>
            <h3 className="font-semibold text-white mb-1">Sale credits your ledger</h3>
            <p className="text-sm text-[#8A92A0]">Every purchase debits the fan (with sales tax added at checkout) and credits your signed wallet ledger.</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-3">
              <span className="text-[#EC008C] font-bold">3</span>
            </div>
            <h3 className="font-semibold text-white mb-1">Day 11 auto-wire</h3>
            <p className="text-sm text-[#8A92A0]">AI treasury fires a Stripe Connect transfer for your 88% (90% Founders) minus Stripe processing + payout fees — on your chosen cadence (Day-11 / biweekly / monthly).</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BankField = ({ label, value, onChange, placeholder, testid, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] uppercase tracking-widest text-[#6A7280] mb-1">{label}</span>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white placeholder-[#4A5260] focus:outline-none focus:border-[#EC008C]/50"
      data-testid={testid}
    />
  </label>
);


// Weekly Summary Component
const WeeklySummary = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/stats/weekly-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(res.data);
    } catch (e) {
      console.error("Failed to fetch weekly summary:", e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  if (!summary) return <div className="card p-8 text-center text-[#8A92A0]">No summary available yet.</div>;

  return (
    <div className="space-y-6" data-testid="weekly-summary">
      <div>
        <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Weekly Summary</h1>
        <p className="text-[#8A92A0]">{summary.period}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-[#8DB87A]" />
            {summary.revenue_change_percent !== 0 && (
              <span className={`flex items-center text-xs font-medium ${summary.revenue_change_percent > 0 ? 'text-[#8DB87A]' : 'text-[#D14A85]'}`}>
                {summary.revenue_change_percent > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(summary.revenue_change_percent)}%
              </span>
            )}
          </div>
          <p className="text-2xl font-['Unbounded'] font-bold text-white">${summary.revenue}</p>
          <p className="text-xs text-[#8A92A0] mt-1">Total Revenue</p>
        </div>
        <div className="card p-5">
          <DollarSign className="w-5 h-5 text-[#EC008C] mb-2" />
          <p className="text-2xl font-['Unbounded'] font-bold text-white">${summary.artist_earnings}</p>
          <p className="text-xs text-[#8A92A0] mt-1">Your Earnings (90%)</p>
        </div>
        <div className="card p-5">
          <Ticket className="w-5 h-5 text-[#FF6BC5] mb-2" />
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{summary.ticket_sales}</p>
          <p className="text-xs text-[#8A92A0] mt-1">Tickets Sold</p>
        </div>
        <div className="card p-5">
          <ShoppingBag className="w-5 h-5 text-[#D14A85] mb-2" />
          <p className="text-2xl font-['Unbounded'] font-bold text-white">{summary.product_sales}</p>
          <p className="text-xs text-[#8A92A0] mt-1">Products Sold</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#D14A85]/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#D14A85]" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{summary.new_subscriptions}</p>
              <p className="text-xs text-[#8A92A0]">New Subscribers</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#FF6BC5]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#FF6BC5]" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">+{summary.new_followers}</p>
              <p className="text-xs text-[#8A92A0]">New Followers</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#EC008C]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#EC008C]" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{summary.total_followers}</p>
              <p className="text-xs text-[#8A92A0]">Total Followers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Promo Codes Management
const PromoCodesManagement = ({ token }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ code: "", discount_percent: "", max_uses: "", applies_to: "all" });

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/promo-codes/my`, { headers: { Authorization: `Bearer ${token}` } });
      setCodes(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/promo-codes`, {
        code: formData.code,
        discount_percent: parseFloat(formData.discount_percent),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        applies_to: formData.applies_to
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Promo code created!");
      setDialogOpen(false);
      setFormData({ code: "", discount_percent: "", max_uses: "", applies_to: "all" });
      fetchCodes();
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Create your Artist Profile first under the Profile tab");
      } else {
        toast.error(e.response?.data?.detail || "Failed to create promo code");
      }
    }
  };

  const handleDeactivate = async (promoId) => {
    try {
      await axios.delete(`${API}/promo-codes/${promoId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Promo code deactivated");
      fetchCodes();
    } catch (e) { toast.error("Failed to deactivate"); }
  };

  return (
    <div className="space-y-6" data-testid="promo-codes-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Promo Codes</h1>
          <p className="text-[#8A92A0]">Create discount codes for your supporters.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="add-promo-btn">
              <Plus className="w-4 h-4 mr-2" /> New Code
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030]">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-white">Create Promo Code</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="text-white">Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="bg-[#050810] border-[#1A2030] text-white font-mono uppercase"
                  placeholder="SUMMER20"
                  required
                  data-testid="promo-code-input"
                />
              </div>
              <div>
                <Label className="text-white">Discount % *</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({...formData, discount_percent: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="20"
                  required
                  data-testid="promo-discount-input"
                />
              </div>
              <div>
                <Label className="text-white">Max Uses (leave empty for unlimited)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                  className="bg-[#050810] border-[#1A2030] text-white"
                  placeholder="100"
                  data-testid="promo-max-uses-input"
                />
              </div>
              <div>
                <Label className="text-white">Applies To</Label>
                <Select value={formData.applies_to} onValueChange={(v) => setFormData({...formData, applies_to: v})}>
                  <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="promo-applies-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                    <SelectItem value="all">All (Tickets & Products)</SelectItem>
                    <SelectItem value="tickets">Tickets Only</SelectItem>
                    <SelectItem value="products">Products Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="submit-promo-btn">
                Create Promo Code
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : codes.length === 0 ? (
        <div className="card p-12 text-center">
          <Tag className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <p className="text-[#8A92A0]">No promo codes yet. Create one to boost sales!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {codes.map((code) => (
            <div key={code.promo_id} className={`card p-5 ${!code.is_active ? 'opacity-50' : ''}`} data-testid={`promo-${code.promo_id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-lg font-bold text-[#EC008C] tracking-wider">{code.code}</span>
                {code.is_active && (
                  <button onClick={() => handleDeactivate(code.promo_id)} className="text-[#8A92A0] hover:text-[#D14A85]" data-testid={`deactivate-${code.promo_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-white font-semibold">{code.discount_percent}% off</span>
                <span className="text-[#8A92A0] capitalize">{code.applies_to}</span>
                <span className="text-[#8A92A0]">{code.times_used}{code.max_uses ? `/${code.max_uses}` : ''} used</span>
              </div>
              {!code.is_active && <span className="text-xs text-[#D14A85] mt-2 block">Deactivated</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Experiences Management Component
const ExperiencesManagement = ({ token }) => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: "", description: "", experience_type: "fan_meet", price: "", duration_minutes: "30", slots_available: "1", payment_link: "",
    meeting_url: "", meeting_provider: "google_meet", scheduled_at: "", instructions: ""
  });

  useEffect(() => { fetchExperiences(); }, []);

  const fetchExperiences = async () => {
    try {
      const res = await axios.get(`${API}/experiences/my`, { headers: { Authorization: `Bearer ${token}` } });
      setExperiences(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/experiences`, {
        title: formData.title,
        description: formData.description,
        experience_type: formData.experience_type,
        price: parseFloat(formData.price),
        duration_minutes: parseInt(formData.duration_minutes),
        slots_available: parseInt(formData.slots_available),
        payment_link: formData.payment_link || null,
        meeting_url: formData.meeting_url || null,
        meeting_provider: formData.meeting_provider || null,
        scheduled_at: formData.scheduled_at || null,
        instructions: formData.instructions || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Experience created!");
      setDialogOpen(false);
      setFormData({ title: "", description: "", experience_type: "fan_meet", price: "", duration_minutes: "30", slots_available: "1", payment_link: "", meeting_url: "", meeting_provider: "google_meet", scheduled_at: "", instructions: "" });
      fetchExperiences();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create experience");
    } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this experience?")) return;
    try {
      await axios.delete(`${API}/experiences/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Experience deleted");
      fetchExperiences();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const typeLabels = { fan_meet: "Supporter Meet", video_call: "1-on-1 Call", backstage: "Backstage" };
  const typeIcons = { fan_meet: Star, video_call: Phone, backstage: Users };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="experiences-management">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-['Unbounded'] text-2xl font-bold text-white">Paid Experiences</h2>
          <p className="text-[#8A92A0] text-sm mt-1">Create fan meets, 1-on-1 calls, and backstage access</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="create-experience-btn">
              <Plus className="w-4 h-4 mr-2" /> New Experience
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030] text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-xl">Create Paid Experience</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-white">Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="Private Supporter Meet & Greet" required data-testid="exp-title-input" />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="Meet me in person at a private venue..." rows={3} data-testid="exp-desc-input" />
              </div>
              <div>
                <Label className="text-white">Type *</Label>
                <Select value={formData.experience_type} onValueChange={(v) => setFormData({...formData, experience_type: v})}>
                  <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="exp-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                    <SelectItem value="fan_meet" className="text-white hover:text-[#EC008C]">Supporter Meet</SelectItem>
                    <SelectItem value="video_call" className="text-white hover:text-[#EC008C]">1-on-1 Video Call</SelectItem>
                    <SelectItem value="backstage" className="text-white hover:text-[#EC008C]">Backstage Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-white">Price (Tokens) *</Label>
                  <Input type="number" step="1" min="1" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="99" required data-testid="exp-price-input" />
                </div>
                <div>
                  <Label className="text-white">Duration (min)</Label>
                  <Input type="number" min="5" value={formData.duration_minutes} onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" data-testid="exp-duration-input" />
                </div>
                <div>
                  <Label className="text-white">Slots</Label>
                  <Input type="number" min="1" value={formData.slots_available} onChange={(e) => setFormData({...formData, slots_available: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" data-testid="exp-slots-input" />
                </div>
              </div>

              {/* Meeting delivery — how the fan actually joins the experience */}
              <div className="border-t border-[#1A2030] pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-[#EC008C]" />
                  <h4 className="font-semibold text-white text-sm">How the fan will join</h4>
                </div>
                <p className="text-xs text-[#6A7280] mb-3">After payment, the fan will see a &quot;Join meeting&quot; button with this link. Use your Calendly link, Google Meet, Zoom, or any URL you control.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white text-xs">Provider</Label>
                    <Select value={formData.meeting_provider} onValueChange={(v) => setFormData({...formData, meeting_provider: v})}>
                      <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="exp-meeting-provider-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                        <SelectItem value="google_meet" className="text-white">Google Meet</SelectItem>
                        <SelectItem value="calendly" className="text-white">Calendly</SelectItem>
                        <SelectItem value="zoom" className="text-white">Zoom</SelectItem>
                        <SelectItem value="custom" className="text-white">Custom URL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white text-xs">Scheduled (optional)</Label>
                    <Input type="datetime-local" value={formData.scheduled_at} onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" data-testid="exp-scheduled-input" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-white text-xs">Meeting URL *</Label>
                  <Input
                    value={formData.meeting_url}
                    onChange={(e) => setFormData({...formData, meeting_url: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="https://calendly.com/your-handle/15min  or  https://meet.google.com/abc-defg-hij"
                    data-testid="exp-meeting-url-input"
                  />
                </div>
                <div className="mt-3">
                  <Label className="text-white text-xs">Instructions for the fan (optional)</Label>
                  <Textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                    className="bg-[#050810] border-[#1A2030] text-white"
                    placeholder="I'll be online 5 minutes before. Please have your camera ready."
                    rows={2}
                    data-testid="exp-instructions-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={creating} className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="exp-submit-btn">
                {creating ? "Creating..." : "Create Experience"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {experiences.length === 0 ? (
        <div className="card p-12 text-center">
          <Star className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">No experiences yet</h3>
          <p className="text-[#8A92A0] text-sm">Create fan meets, video calls, or backstage experiences and start earning.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {experiences.map((exp) => {
            const TypeIcon = typeIcons[exp.experience_type] || Star;
            const slotsLeft = exp.slots_available - exp.slots_booked;
            return (
              <div key={exp.experience_id} className="card p-5" data-testid={`experience-${exp.experience_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#D14A85]/10 flex items-center justify-center">
                      <TypeIcon className="w-5 h-5 text-[#D14A85]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{exp.title}</h3>
                      <span className="text-xs text-[#8A92A0]">{typeLabels[exp.experience_type] || exp.experience_type}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(exp.experience_id)} className="text-[#8A92A0] hover:text-[#D14A85] transition-colors" data-testid={`delete-exp-${exp.experience_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {exp.description && <p className="text-[#8A92A0] text-sm mb-3 line-clamp-2">{exp.description}</p>}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-[#EC008C] font-bold text-lg">${exp.price}</span>
                  <span className="text-[#8A92A0]">{exp.duration_minutes} min</span>
                  <span className={`${slotsLeft > 0 ? 'text-[#8DB87A]' : 'text-[#D14A85]'}`}>
                    {slotsLeft > 0 ? `${slotsLeft}/${exp.slots_available} slots` : 'Sold out'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// AI Messages Management Component  
const AIMessagesManagement = ({ token }) => {
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: "", description: "", price: "", scene_style: "fantasy"
  });

  useEffect(() => { fetchOfferings(); }, []);

  const fetchOfferings = async () => {
    try {
      const res = await axios.get(`${API}/ai-messages/offerings/my`, { headers: { Authorization: `Bearer ${token}` } });
      setOfferings(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/ai-messages/offerings`, {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        scene_style: formData.scene_style
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("AI Message offering created!");
      setDialogOpen(false);
      setFormData({ title: "", description: "", price: "", scene_style: "fantasy" });
      fetchOfferings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create offering");
    } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this AI message offering?")) return;
    try {
      await axios.delete(`${API}/ai-messages/offerings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Offering deleted");
      fetchOfferings();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const styleLabels = { fantasy: "Fantasy", concert: "Concert", space: "Space", nature: "Nature", cyberpunk: "Cyberpunk" };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="ai-messages-management">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-['Unbounded'] text-2xl font-bold text-white">AI Personalization</h2>
          <p className="text-[#8A92A0] text-sm mt-1">Create AI-powered personalized messages with custom scenes for your supporters</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="create-ai-offering-btn">
              <Plus className="w-4 h-4 mr-2" /> New AI Offering
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-xl">Create AI Message Offering</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-white">Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="Personal AI Message & Custom Scene" required data-testid="ai-title-input" />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="Get a personal message from me plus a custom AI-generated scene..." rows={3} data-testid="ai-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Price (Tokens) *</Label>
                  <Input type="number" step="1" min="1" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="10" required data-testid="ai-price-input" />
                </div>
                <div>
                  <Label className="text-white">Scene Style</Label>
                  <Select value={formData.scene_style} onValueChange={(v) => setFormData({...formData, scene_style: v})}>
                    <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="ai-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                      <SelectItem value="fantasy" className="text-white">Fantasy</SelectItem>
                      <SelectItem value="concert" className="text-white">Concert</SelectItem>
                      <SelectItem value="space" className="text-white">Space</SelectItem>
                      <SelectItem value="nature" className="text-white">Nature</SelectItem>
                      <SelectItem value="cyberpunk" className="text-white">Cyberpunk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={creating} className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="ai-submit-btn">
                {creating ? "Creating..." : "Create Offering"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {offerings.length === 0 ? (
        <div className="card p-12 text-center">
          <Sparkles className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">No AI offerings yet</h3>
          <p className="text-[#8A92A0] text-sm">Create personalized AI message offerings and fans will receive a custom message + AI-generated scene with their name.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {offerings.map((off) => (
            <div key={off.offering_id} className="card p-5" data-testid={`ai-offering-${off.offering_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D14A85]/20 to-[#EC008C]/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#EC008C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{off.title}</h3>
                    <span className="text-xs text-[#8A92A0]">{styleLabels[off.scene_style] || off.scene_style} scene</span>
                  </div>
                </div>
                <button onClick={() => handleDelete(off.offering_id)} className="text-[#8A92A0] hover:text-[#D14A85] transition-colors" data-testid={`delete-ai-${off.offering_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {off.description && <p className="text-[#8A92A0] text-sm mb-3 line-clamp-2">{off.description}</p>}
              <div className="flex items-center gap-4 text-sm">
                <span className="text-[#EC008C] font-bold text-lg">${off.price}</span>
                <span className="text-[#8A92A0]">{off.total_generated} generated</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Collectibles Management Component
const CollectiblesManagement = ({ token }) => {
  const [collectibles, setCollectibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", price: "", total_supply: "100", category: "artwork" });

  useEffect(() => { fetchCollectibles(); }, []);

  const fetchCollectibles = async () => {
    try {
      const res = await axios.get(`${API}/collectibles/my`, { headers: { Authorization: `Bearer ${token}` } });
      setCollectibles(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/collectibles`, {
        title: formData.title, description: formData.description,
        price: parseFloat(formData.price), total_supply: parseInt(formData.total_supply),
        category: formData.category
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Collectible drop created!");
      setDialogOpen(false);
      setFormData({ title: "", description: "", price: "", total_supply: "100", category: "artwork" });
      fetchCollectibles();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this collectible?")) return;
    try {
      await axios.delete(`${API}/collectibles/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchCollectibles();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const categoryLabels = { artwork: "Artwork", signed_content: "Signed Content", exclusive_track: "Exclusive Track", limited_merch: "Limited Merch" };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="collectibles-management">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-['Unbounded'] text-2xl font-bold text-white">Digital Collectibles</h2>
          <p className="text-[#8A92A0] text-sm mt-1">Create limited-edition drops for your supporters</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="create-collectible-btn">
              <Plus className="w-4 h-4 mr-2" /> New Drop
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0E18] border-[#1A2030] text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-['Unbounded'] text-xl">Create Collectible Drop</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-white">Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="Exclusive Signed Artwork #1" required data-testid="col-title-input" />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="A one-of-a-kind digital artwork..." rows={3} data-testid="col-desc-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-white">Price (Tokens) *</Label>
                  <Input type="number" step="1" min="1" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" placeholder="25" required data-testid="col-price-input" />
                </div>
                <div>
                  <Label className="text-white">Supply</Label>
                  <Input type="number" min="1" max="10000" value={formData.total_supply} onChange={(e) => setFormData({...formData, total_supply: e.target.value})} className="bg-[#050810] border-[#1A2030] text-white" data-testid="col-supply-input" />
                </div>
                <div>
                  <Label className="text-white">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="col-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
                      <SelectItem value="artwork" className="text-white">Artwork</SelectItem>
                      <SelectItem value="signed_content" className="text-white">Signed Content</SelectItem>
                      <SelectItem value="exclusive_track" className="text-white">Exclusive Track</SelectItem>
                      <SelectItem value="limited_merch" className="text-white">Limited Merch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={creating} className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="col-submit-btn">
                {creating ? "Creating..." : "Create Drop"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {collectibles.length === 0 ? (
        <div className="card p-12 text-center">
          <Star className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">No collectibles yet</h3>
          <p className="text-[#8A92A0] text-sm">Create limited-edition digital drops — artwork, signed content, exclusive tracks.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {collectibles.map((col) => {
            const remaining = col.total_supply - col.sold_count;
            return (
              <div key={col.collectible_id} className="card p-5" data-testid={`collectible-${col.collectible_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#EC008C]/10 flex items-center justify-center">
                      <Star className="w-5 h-5 text-[#EC008C]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{col.title}</h3>
                      <span className="text-xs text-[#8A92A0]">{categoryLabels[col.category] || col.category}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(col.collectible_id)} className="text-[#8A92A0] hover:text-[#D14A85] transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
                {col.description && <p className="text-[#8A92A0] text-sm mb-3 line-clamp-2">{col.description}</p>}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-[#EC008C] font-bold text-lg">${col.price}</span>
                  <span className="text-[#8A92A0]">{col.sold_count}/{col.total_supply} sold</span>
                  <span className={remaining > 0 ? "text-[#8DB87A]" : "text-[#D14A85]"}>{remaining > 0 ? `${remaining} left` : "Sold out"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Admin Panel Component
// Physical Merchandise toggle — flips db.platform_settings.physical_merchandise_enabled.
// When OFF the Product Form hides Flow B (Physical) and artists can only create digital products.
const PhysicalMerchandiseToggle = ({ token }) => {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/platform-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setEnabled(!!r.data?.physical_merchandise_enabled);
      } catch (e) {
        if (!cancelled) setEnabled(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const toggle = async () => {
    setSaving(true);
    const next = !enabled;
    try {
      await axios.post(
        `${API}/admin/platform-settings`,
        { physical_merchandise_enabled: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEnabled(next);
      toast.success(
        next
          ? "Physical merchandise is now visible in the Product Form."
          : "Physical merchandise hidden. Artists can only create digital products now."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update setting");
    } finally {
      setSaving(false);
    }
  };

  if (enabled === null) {
    return (
      <div className="card p-4 text-[#8A92A0] text-sm" data-testid="physical-merch-toggle-loading">
        Loading platform settings…
      </div>
    );
  }

  return (
    <div className="card p-5" data-testid="physical-merch-toggle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F0C464]/10 border border-[#F0C464]/30 flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5 text-[#F0C464]" />
          </div>
          <div>
            <h2 className="font-['Unbounded'] text-lg font-semibold text-white">Enable Physical Merchandise</h2>
            <p className="text-xs text-[#8A92A0] max-w-lg mt-1">
              Turn <strong className="text-white">OFF</strong> to hide Flow B in the Add Product form. Artists will only be able to create digital products (Digital Downloads, Albums, Livestream Passes, Remix Challenges) — no shipping, no inventory to manage.
            </p>
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          role="switch"
          aria-checked={enabled}
          className={`relative w-14 h-8 rounded-full transition ${enabled ? "bg-[#EC008C]" : "bg-[#1A2030]"} ${saving ? "opacity-60" : ""}`}
          data-testid="physical-merch-toggle-btn"
        >
          <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${enabled ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>
      <p className="mt-3 text-[11px] text-[#6A7280]">
        Current state: <span className={`font-semibold ${enabled ? "text-[#8DB87A]" : "text-[#F0C464]"}`} data-testid="physical-merch-toggle-state">{enabled ? "Physical Merch ON" : "Digital-only mode"}</span>
      </p>
    </div>
  );
};

// Surge Auto-Approve KYC toggle — flips db.platform_settings.auto_approve_kyc.
// When ON, an artist's ID+selfie scan is auto-approved if faces_match,
// id_appears_authentic, no scan_error, AND deepfake_confidence < 50 (the
// strict path still triggers on < 20 too). Crashed scans NEVER auto-approve.
const AutoApproveKycToggle = ({ token }) => {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/platform-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setEnabled(!!r.data?.auto_approve_kyc);
      } catch (e) {
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const toggle = async () => {
    setSaving(true);
    const next = !enabled;
    try {
      await axios.post(
        `${API}/admin/platform-settings`,
        { auto_approve_kyc: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEnabled(next);
      toast.success(
        next
          ? "Surge Auto-Approve ON — KYC scans with clean identity checks & deepfake_confidence < 50 auto-approve."
          : "Surge Auto-Approve OFF — strict AI path only (deepfake_confidence < 20)."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update setting");
    } finally {
      setSaving(false);
    }
  };

  if (enabled === null) {
    return (
      <div className="card p-4 text-[#8A92A0] text-sm" data-testid="auto-approve-kyc-toggle-loading">
        Loading platform settings…
      </div>
    );
  }

  return (
    <div className="card p-5" data-testid="auto-approve-kyc-toggle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/30 flex items-center justify-center flex-shrink-0">
            <BadgeCheck className="w-5 h-5 text-[#EC008C]" />
          </div>
          <div>
            <h2 className="font-['Unbounded'] text-lg font-semibold text-white">Surge Auto-Approve KYC</h2>
            <p className="text-xs text-[#8A92A0] max-w-lg mt-1">
              Turn <strong className="text-white">ON</strong> to auto-approve artist KYC scans that pass identity checks (faces match, ID authentic, no scan error) with deepfake_confidence below 50. Strict path (&lt; 20) still runs when this is off. Crashed scans <strong className="text-[#F0C464]">never</strong> auto-approve.
            </p>
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          role="switch"
          aria-checked={enabled}
          className={`relative w-14 h-8 rounded-full transition ${enabled ? "bg-[#EC008C]" : "bg-[#1A2030]"} ${saving ? "opacity-60" : ""}`}
          data-testid="auto-approve-kyc-toggle-btn"
        >
          <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${enabled ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>
      <p className="mt-3 text-[11px] text-[#6A7280]">
        Current state: <span className={`font-semibold ${enabled ? "text-[#EC008C]" : "text-[#8DB87A]"}`} data-testid="auto-approve-kyc-toggle-state">{enabled ? "Surge Auto-Approve ON" : "Strict KYC only"}</span>
      </p>
    </div>
  );
};

const AdminPanel = ({ token, user }) => {
  const [artists, setArtists] = useState([]);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthReport, setHealthReport] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [runningCheck, setRunningCheck] = useState(false);
  const [failedLogins, setFailedLogins] = useState(null);
  const [failedLoading, setFailedLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    fetchArtists();
    fetchVerificationRequests();
    fetchLatestHealth();
    fetchFailedLogins();
  }, []);

  const fetchLatestHealth = async () => {
    setHealthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/health-reports?limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.reports?.length) setHealthReport(res.data.reports[0]);
    } catch (e) {
      console.error("Health fetch failed:", e);
    } finally {
      setHealthLoading(false);
    }
  };

  const runHealthCheckNow = async (remediate = false) => {
    setRunningCheck(true);
    try {
      const res = await axios.post(`${API}/admin/health-check/run-now`, {}, {
        params: remediate === true ? { remediate: true } : {},
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealthReport(res.data);
      const fixCount = Object.values(res.data?.auto_fixes_applied || {}).reduce((a, b) => a + b, 0);
      const pendingCount = Object.values(res.data?.pending_cleanup || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `AI health check complete. ${fixCount > 0 ? `Cleaned ${fixCount} item(s).` : pendingCount > 0 ? `${pendingCount} item(s) waiting for your cleanup approval.` : "All clean."}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Health check failed");
    } finally {
      setRunningCheck(false);
    }
  };

  const fetchFailedLogins = async () => {
    setFailedLoading(true);
    try {
      const res = await axios.get(`${API}/admin/failed-login-report?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFailedLogins(res.data);
    } catch (e) {
      console.error("Failed-login fetch error:", e);
    } finally {
      setFailedLoading(false);
    }
  };

  const emailFailedLoginReport = async () => {
    setEmailing(true);
    try {
      const res = await axios.post(`${API}/admin/failed-login-report/email?days=30`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sentCount = res.data?.sent_count ?? 0;
      const total = res.data?.emailed_to?.length ?? 0;
      if (sentCount === total) {
        toast.success(`Report emailed to all ${total} recipient(s)`);
      } else if (sentCount > 0) {
        const failed = res.data.delivery.filter(d => !d.sent).map(d => d.to).join(", ");
        toast.warning(`Sent to ${sentCount}/${total}. Failed: ${failed}. (Resend test-mode? Verify your domain.)`);
      } else {
        toast.error("All sends failed — verify a domain in Resend dashboard.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  const fetchArtists = async () => {
    try {
      const res = await axios.get(`${API}/admin/artists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtists(res.data);
    } catch (e) {
      console.error("Admin fetch failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/verification-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationRequests(res.data);
    } catch (e) {
      console.error("Verification requests fetch failed:", e);
    }
  };

  const decideRequest = async (requestId, approve) => {
    try {
      await axios.post(`${API}/admin/verification-requests/${requestId}/decide?approve=${approve}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationRequests(prev => prev.filter(r => r.request_id !== requestId));
      toast.success(approve ? "Artist verified" : "Request rejected");
      // Refresh artists if approved (their is_verified status changed)
      if (approve) fetchArtists();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    }
  };

  const toggleVerification = async (artistId) => {
    try {
      const res = await axios.post(`${API}/admin/verify-artist/${artistId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtists(prev => prev.map(a => 
        a.artist_id === artistId ? { ...a, is_verified: res.data.is_verified } : a
      ));
      toast.success(res.data.is_verified ? "Artist verified!" : "Verification removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update verification");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <AdminNavigation />
      <div>
        <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Admin Panel</h1>
        <p className="text-[#8A92A0]">Manage artists and verification badges</p>
      </div>

      {/* Platform Feature Flags */}
      <PhysicalMerchandiseToggle token={token} />
      <AutoApproveKycToggle token={token} />

      {/* SYSTEM PULSE — AI-driven 24h health widget */}
      <div className="card p-6 relative overflow-hidden" data-testid="system-pulse-widget">
        <div className="absolute inset-0 bg-gradient-to-br from-[#EC008C]/5 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/30 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-[#EC008C]" />
              </div>
              <div>
                <h2 className="font-['Unbounded'] text-lg font-semibold text-white">System Pulse</h2>
                <p className="text-xs text-[#8A92A0]">Automated 24h AI health check &amp; corruption self-heal</p>
              </div>
            </div>
            <Button
              onClick={runHealthCheckNow}
              disabled={runningCheck}
              className="bg-[#EC008C] text-black hover:bg-[#FF3399] font-semibold rounded-full px-4 h-9"
              data-testid="run-health-check-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${runningCheck ? "animate-spin" : ""}`} />
              {runningCheck ? "Running…" : "Run Check Now"}
            </Button>
          </div>

          {healthLoading && !healthReport ? (
            <p className="text-[#8A92A0] text-sm">Loading latest report…</p>
          ) : !healthReport ? (
            <p className="text-[#8A92A0] text-sm">No report yet — click <strong className="text-white">Run Check Now</strong> to generate the first one.</p>
          ) : (
            <div className="space-y-4">
              {/* Verdict */}
              <div className="flex items-center gap-3 flex-wrap">
                {healthReport.ai_verdict === "healthy" ? (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8DB87A]/15 text-[#8DB87A] text-sm font-semibold" data-testid="health-verdict">
                    <CheckCircle2 className="w-4 h-4" /> Healthy
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EC008C]/15 text-[#EC008C] text-sm font-semibold" data-testid="health-verdict">
                    <AlertCircle className="w-4 h-4" /> Attention Needed
                  </span>
                )}
                <span className="text-xs text-[#8A92A0]">
                  Last check: {new Date(healthReport.created_at).toLocaleString()}
                </span>
              </div>

              {/* AI summary */}
              <p className="text-[#8A92A0] leading-relaxed text-sm bg-[#050810] border border-[#1A2030] rounded-lg p-4" data-testid="health-summary">
                <span className="text-white font-semibold">AI summary: </span>
                {healthReport.ai_summary}
              </p>

              {/* Auto-fixes applied */}
              {healthReport.auto_fixes_applied && Object.keys(healthReport.auto_fixes_applied).length > 0 && (
                <div className="bg-[#8DB87A]/5 border border-[#8DB87A]/30 rounded-lg p-4" data-testid="health-fixes">
                  <p className="text-[#8DB87A] font-semibold text-sm mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Auto-fixes applied this run
                  </p>
                  <ul className="text-[#8A92A0] text-sm space-y-1 list-disc list-inside">
                    {Object.entries(healthReport.auto_fixes_applied).map(([k, v]) => (
                      <li key={k}><span className="text-white">{v}</span> × {k.replace(/_/g, " ")}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Pending cleanup (report-only mode) — owner must explicitly approve */}
              {healthReport.pending_cleanup && Object.keys(healthReport.pending_cleanup).length > 0 && (
                <div className="bg-[#F5C451]/5 border border-[#F5C451]/30 rounded-lg p-4" data-testid="health-pending-cleanup">
                  <p className="text-[#F5C451] font-semibold text-sm mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Cleanup waiting for your approval
                  </p>
                  <ul className="text-[#8A92A0] text-sm space-y-1 list-disc list-inside mb-3">
                    {Object.entries(healthReport.pending_cleanup).map(([k, v]) => (
                      <li key={k}><span className="text-white">{v}</span> × {k.replace(/_removed$/, "").replace(/_/g, " ")}</li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => runHealthCheckNow(true)}
                    disabled={runningCheck}
                    variant="outline"
                    className="border-[#F5C451]/50 text-[#F5C451] hover:bg-[#F5C451]/10 rounded-full h-8 px-4 text-xs"
                    data-testid="health-run-cleanup-btn"
                  >
                    Run cleanup now
                  </Button>
                </div>
              )}

              {/* AI suggested actions */}
              {healthReport.ai_actions && healthReport.ai_actions.length > 0 && (
                <div className="bg-[#EC008C]/5 border border-[#EC008C]/20 rounded-lg p-4" data-testid="health-actions">
                  <p className="text-[#EC008C] font-semibold text-sm mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> AI-recommended manual actions
                  </p>
                  <ul className="text-[#8A92A0] text-sm space-y-1 list-decimal list-inside">
                    {healthReport.ai_actions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}

              {/* Key metrics */}
              {healthReport.metrics && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  <div className="bg-[#050810] border border-[#1A2030] rounded-lg p-3" data-testid="metric-users">
                    <p className="text-xs text-[#8A92A0]">Total Users</p>
                    <p className="text-xl font-bold text-white font-['Unbounded']">{healthReport.metrics.users?.total ?? "-"}</p>
                  </div>
                  <div className="bg-[#050810] border border-[#1A2030] rounded-lg p-3" data-testid="metric-artists">
                    <p className="text-xs text-[#8A92A0]">Artists</p>
                    <p className="text-xl font-bold text-white font-['Unbounded']">{healthReport.metrics.users?.artists_total ?? "-"}</p>
                  </div>
                  <div className="bg-[#050810] border border-[#1A2030] rounded-lg p-3" data-testid="metric-tx">
                    <p className="text-xs text-[#8A92A0]">Tx (24h)</p>
                    <p className="text-xl font-bold text-white font-['Unbounded']">{healthReport.metrics.commerce?.transactions_24h ?? "-"}</p>
                  </div>
                  <div className="bg-[#050810] border border-[#1A2030] rounded-lg p-3" data-testid="metric-fails">
                    <p className="text-xs text-[#8A92A0]">Failed Logins</p>
                    <p className="text-xl font-bold text-white font-['Unbounded']">{healthReport.metrics.auth?.failed_logins_24h ?? "-"}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* FAILED LOGIN ATTEMPTS — security audit */}
      <div className="card p-6" data-testid="failed-login-widget">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/30 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-[#EC008C]" />
            </div>
            <div>
              <h2 className="font-['Unbounded'] text-lg font-semibold text-white">Failed Login Attempts</h2>
              <p className="text-xs text-[#8A92A0]">Past 30 days &middot; aggregated from audit log</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchFailedLogins}
              variant="ghost"
              size="sm"
              disabled={failedLoading}
              className="text-[#8A92A0] hover:text-white"
              data-testid="refresh-failed-logins-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${failedLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={emailFailedLoginReport}
              disabled={emailing}
              size="sm"
              className="bg-[#EC008C] text-black hover:bg-[#FF3399] font-semibold rounded-full"
              data-testid="email-failed-logins-btn"
            >
              <Mail className={`w-4 h-4 mr-2 ${emailing ? "animate-pulse" : ""}`} />
              {emailing ? "Sending…" : "Email Me Report"}
            </Button>
          </div>
        </div>

        {failedLoading && !failedLogins ? (
          <p className="text-[#8A92A0] text-sm">Loading…</p>
        ) : !failedLogins ? (
          <p className="text-[#8A92A0] text-sm">No data yet — click Refresh.</p>
        ) : (
          <>
            {/* Stats banner */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-[#050810] border border-[#1A2030] rounded-lg p-4" data-testid="failed-total-attempts">
                <p className="text-xs text-[#8A92A0] mb-1">Total Attempts</p>
                <p className="text-3xl font-bold text-white font-['Unbounded']">{failedLogins.total_attempts}</p>
              </div>
              <div className="bg-[#050810] border border-[#1A2030] rounded-lg p-4" data-testid="failed-unique-emails">
                <p className="text-xs text-[#8A92A0] mb-1">Unique Emails</p>
                <p className="text-3xl font-bold text-[#EC008C] font-['Unbounded']">{failedLogins.unique_emails}</p>
              </div>
            </div>

            {/* Per-email breakdown */}
            {failedLogins.rows.length === 0 ? (
              <p className="text-[#8DB87A] text-sm flex items-center gap-2 bg-[#8DB87A]/5 border border-[#8DB87A]/30 rounded-lg p-4">
                <CheckCircle2 className="w-4 h-4" /> Quiet skies — no failed login attempts in this window.
              </p>
            ) : (
              <div className="overflow-x-auto bg-[#050810] border border-[#1A2030] rounded-lg">
                <table className="w-full text-sm" data-testid="failed-login-table">
                  <thead>
                    <tr className="border-b border-[#1A2030]">
                      <th className="text-left p-3 text-xs font-medium text-[#8A92A0]">Email</th>
                      <th className="text-right p-3 text-xs font-medium text-[#8A92A0]">Attempts</th>
                      <th className="text-left p-3 text-xs font-medium text-[#8A92A0] hidden sm:table-cell">Last Attempt</th>
                      <th className="text-left p-3 text-xs font-medium text-[#8A92A0]">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedLogins.rows.slice(0, 50).map((r) => (
                      <tr key={r.email} className="border-b border-[#1A2030] last:border-0" data-testid={`failed-login-row-${r.email}`}>
                        <td className="p-3 text-white break-all">{r.email}</td>
                        <td className="p-3 text-right">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${r.count >= 5 ? "bg-[#EC008C]/20 text-[#EC008C]" : "bg-[#1A2030] text-[#8A92A0]"}`}>
                            {r.count}
                          </span>
                        </td>
                        <td className="p-3 text-[#8A92A0] text-xs hidden sm:table-cell">{new Date(r.last_attempt).toLocaleString()}</td>
                        <td className="p-3">
                          {r.was_admin_attempt ? (
                            <span className="text-xs font-semibold text-[#EC008C]">ADMIN</span>
                          ) : (
                            <span className="text-xs text-[#8A92A0]">user</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {failedLogins.rows.length > 50 && (
                  <p className="text-xs text-[#8A92A0] p-3 text-center border-t border-[#1A2030]">
                    Showing top 50 of {failedLogins.rows.length} unique emails &middot; use &ldquo;Email Me Report&rdquo; for full list.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-y border-[#1A2030] py-4" data-testid="admin-kyc-navigation">
        <h2 className="text-base md:text-lg font-semibold">Identity verification</h2>
        <Link to="/admin/verifications" data-testid="admin-open-kyc-queue" className="inline-flex items-center gap-2 text-sm text-[#EC008C] hover:text-[#FF3399] transition-colors">
          <ShieldCheck className="w-4 h-4" /> Review ID submissions
        </Link>
      </div>

      {/* Public artist badge requests are separate from ID verification. */}
      {verificationRequests.length > 0 && (
        <div className="card p-6" data-testid="verification-requests-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Unbounded'] text-lg font-semibold text-white flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-[#EC008C]" />
              Artist badge requests
            </h2>
            <span className="px-3 py-1 rounded-full bg-[#EC008C]/10 text-[#EC008C] text-xs font-bold">{verificationRequests.length} pending</span>
          </div>
          <div className="space-y-3">
            {verificationRequests.map((req) => (
              <div key={req.request_id} className="flex items-center justify-between bg-[#050810] rounded-lg p-4 border border-[#1A2030]" data-testid={`verification-request-${req.request_id}`}>
                <div>
                  <p className="text-white font-medium">{req.stage_name}</p>
                  <p className="text-xs text-[#8A92A0]">Requested {new Date(req.created_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => decideRequest(req.request_id, true)}
                    className="bg-[#8DB87A] text-[#000000] hover:bg-[#A4C896]"
                    data-testid={`approve-${req.request_id}`}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => decideRequest(req.request_id, false)}
                    variant="outline"
                    className="border-[#1A2030] text-[#8A92A0] hover:text-[#D14A85]"
                    data-testid={`reject-${req.request_id}`}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1A2030]">
              <th className="text-left p-4 text-sm font-medium text-[#8A92A0]">Artist</th>
              <th className="text-left p-4 text-sm font-medium text-[#8A92A0]">Genre</th>
              <th className="text-left p-4 text-sm font-medium text-[#8A92A0]">Followers</th>
              <th className="text-left p-4 text-sm font-medium text-[#8A92A0]">Status</th>
              <th className="text-right p-4 text-sm font-medium text-[#8A92A0]">Action</th>
            </tr>
          </thead>
          <tbody>
            {artists.map((artist) => (
              <tr key={artist.artist_id} className="border-b border-[#1A2030] last:border-0" data-testid={`admin-artist-${artist.artist_id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1A2030] flex items-center justify-center overflow-hidden">
                      {artist.profile_image ? (
                        <img src={artist.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-[#8A92A0]" />
                      )}
                    </div>
                    <span className="text-white font-medium">{artist.stage_name}</span>
                  </div>
                </td>
                <td className="p-4 text-[#8A92A0] text-sm">{artist.genre || "-"}</td>
                <td className="p-4 text-[#8A92A0] text-sm">{artist.follower_count || 0}</td>
                <td className="p-4">
                  {artist.is_verified ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#EC008C]/10 text-[#EC008C] text-xs font-medium">
                      <BadgeCheck className="w-3.5 h-3.5" /> Verified
                    </span>
                  ) : (
                    <span className="text-xs text-[#6A7280]">Unverified</span>
                  )}
                </td>
                <td className="p-4 text-right">
                  <Button
                    onClick={() => toggleVerification(artist.artist_id)}
                    size="sm"
                    className={artist.is_verified 
                      ? "bg-[#1A2030] text-[#8A92A0] hover:bg-[#3F3F46]" 
                      : "bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                    }
                    data-testid={`verify-btn-${artist.artist_id}`}
                  >
                    {artist.is_verified ? "Remove" : "Verify"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {artists.length === 0 && (
          <div className="p-8 text-center text-[#8A92A0]">No artists registered yet.</div>
        )}
      </div>
    </div>
  );
};

// Notification Settings
const CURRENCY_OPTIONS = [
  { code: "usd", symbol: "$", label: "USD — US Dollar" },
  { code: "eur", symbol: "\u20ac", label: "EUR — Euro" },
  { code: "gbp", symbol: "\u00a3", label: "GBP — British Pound" },
  { code: "cad", symbol: "C$", label: "CAD — Canadian Dollar" },
  { code: "aud", symbol: "A$", label: "AUD — Australian Dollar" },
  { code: "ngn", symbol: "\u20a6", label: "NGN — Nigerian Naira" },
  { code: "inr", symbol: "\u20b9", label: "INR — Indian Rupee" },
];

const NotificationSettings = ({ token }) => {
  const [prefs, setPrefs] = useState({
    email_new_follower: true,
    email_new_sale: true,
    email_new_comment: true,
    email_weekly_summary: true
  });
  const [preferredCurrency, setPreferredCurrency] = useState("usd");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCurrency, setSavingCurrency] = useState(false);

  useEffect(() => { fetchPrefs(); fetchCurrency(); }, []);

  const fetchPrefs = async () => {
    try {
      const res = await axios.get(`${API}/settings/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setPrefs(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchCurrency = async () => {
    try {
      const res = await axios.get(`${API}/settings/currency`, { headers: { Authorization: `Bearer ${token}` } });
      setPreferredCurrency(res.data.preferred_currency || "usd");
    } catch (e) { console.error(e); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, prefs, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Notification preferences saved!");
    } catch (e) { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const saveCurrency = async (code) => {
    setPreferredCurrency(code);
    setSavingCurrency(true);
    try {
      await axios.put(`${API}/settings/currency?preferred_currency=${code}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Currency preference saved!");
    } catch (e) { toast.error("Failed to save currency"); } finally { setSavingCurrency(false); }
  };

  const toggles = [
    { key: "email_new_follower", label: "New Follower", desc: "Get notified when someone follows you" },
    { key: "email_new_sale", label: "New Sale", desc: "Get notified when you make a sale (ticket, product, subscription)" },
    { key: "email_new_comment", label: "New Comment", desc: "Get notified when someone comments on your content" },
    { key: "email_weekly_summary", label: "Weekly Summary", desc: "Receive a weekly earnings and growth summary" },
  ];

  if (loading) return <div className="flex justify-center py-20"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6" data-testid="notification-settings">
      <div>
        <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Notification Settings</h1>
        <p className="text-[#8A92A0]">Choose what notifications you want to receive.</p>
      </div>
      <div className="card p-6 space-y-5">
        {toggles.map(t => (
          <div key={t.key} className="flex items-center justify-between py-2">
            <div>
              <p className="text-white font-medium">{t.label}</p>
              <p className="text-sm text-[#6A7280]">{t.desc}</p>
            </div>
            <button
              onClick={() => setPrefs(p => ({ ...p, [t.key]: !p[t.key] }))}
              data-testid={`toggle-${t.key}`}
              className={`w-11 h-6 rounded-full transition-colors relative ${prefs[t.key] ? 'bg-[#EC008C]' : 'bg-[#1A2030]'}`}
            >
              <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform absolute top-0.5 ${prefs[t.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="save-notif-prefs">
        {saving ? "Saving..." : "Save Preferences"}
      </Button>

      {/* Currency Preference */}
      <div className="pt-4">
        <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-1">Currency Preference</h2>
        <p className="text-[#8A92A0] text-sm mb-4">Choose your default currency for all checkouts.</p>
        <div className="card p-6">
          <div className="flex flex-wrap gap-3" data-testid="currency-preference-selector">
            {CURRENCY_OPTIONS.map(c => (
              <button
                key={c.code}
                onClick={() => saveCurrency(c.code)}
                disabled={savingCurrency}
                data-testid={`pref-currency-${c.code}`}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                  preferredCurrency === c.code
                    ? "bg-[#EC008C]/10 border-[#EC008C] text-[#EC008C] shadow-[0_0_12px_rgba(255,51,0,0.15)]"
                    : "bg-[#0A0E18] border-[#1A2030] text-[#8A92A0] hover:border-[#EC008C]/40"
                }`}
              >
                <span className="font-semibold">{c.symbol}</span> {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mantra Suite */}
      <div className="pt-4">
        <MantraSettings token={token} />
      </div>
    </div>
  );
};

// Support Inbox (Admin) — tabbed: Messages | AI Conversations | Users | Reports | Audit Log
const SupportInbox = ({ token }) => {
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [aiSessions, setAiSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [reportStatus, setReportStatus] = useState("pending");
  const [userSearch, setUserSearch] = useState("");
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  useEffect(() => {
    if (tab === "messages") fetchMessages();
    else if (tab === "ai") fetchAiSessions();
    else if (tab === "users") fetchUsers();
    else if (tab === "reports") fetchReports(reportStatus);
    else if (tab === "audit") fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-log?limit=200`, { headers: { Authorization: `Bearer ${token}` } });
      setAuditLogs(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/support/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchAiSessions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-conversations`, { headers: { Authorization: `Bearer ${token}` } });
      setAiSessions(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchUsers = async (q = "", role = "") => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q, role }
      });
      setUsers(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchReports = async (status = "pending") => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status }
      });
      setReports(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const decideReport = async (reportId, action) => {
    try {
      await axios.post(`${API}/admin/reports/${reportId}/decide?action=${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(action === "remove_content" ? "Content removed" : "Report dismissed");
      fetchReports(reportStatus);
    } catch (e) {
      toast.error("Failed to update report");
    }
  };

  const downloadUsersCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/users.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stardomfans_users_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const sendReply = async (supportId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/support/messages/${supportId}/reply`, { text: replyText }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Reply sent!");
      setReplyText("");
      setReplyingTo(null);
      fetchMessages();
    } catch (e) { toast.error("Failed to send reply"); }
  };

  const TabBtn = ({ id, label, count }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === id ? "bg-[#EC008C] text-[#000000]" : "bg-[#0A0E18] text-[#8A92A0] hover:text-white border border-[#1A2030]"}`}
      data-testid={`tab-${id}`}
    >
      {label}{count != null && <span className="ml-2 text-xs opacity-70">({count})</span>}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="support-inbox">
      <div>
        <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Support Inbox</h1>
        <p className="text-[#8A92A0]">Messages, AI conversations, full user directory, and moderation reports.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <TabBtn id="messages" label="Support Messages" count={messages.length || undefined} />
        <TabBtn id="ai" label="AI Conversations" count={aiSessions.length || undefined} />
        <TabBtn id="users" label="All Users" count={users.length || undefined} />
        <TabBtn id="reports" label="Reports" count={reports.length || undefined} />
        <TabBtn id="audit" label="Audit Log" count={auditLogs.length || undefined} />
      </div>

      {loading ? <div className="flex justify-center py-20"><div className="spinner"></div></div> : (
        <>
          {tab === "messages" && (
            messages.length === 0 ? (
              <div className="card p-12 text-center text-[#8A92A0]">No support messages yet.</div>
            ) : (
              <div className="space-y-3">
                {messages.map(msg => (
                  <div key={msg.support_id} className="card p-5" data-testid={`support-msg-${msg.support_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="text-white font-semibold">{msg.name}</span>
                        <span className="text-[#6A7280] text-sm ml-2">{msg.email}</span>
                        {msg.type === "appointment" && <span className="ml-2 px-2 py-0.5 rounded text-xs bg-[#EC008C]/10 text-[#EC008C]">appointment</span>}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs ${msg.status === 'replied' ? 'bg-[#8DB87A]/10 text-[#8DB87A]' : 'bg-[#D14A85]/10 text-[#D14A85]'}`}>
                        {msg.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#6A7280] mb-2">{new Date(msg.created_at).toLocaleString()}</p>
                    <p className="text-[#8A92A0] mb-3 whitespace-pre-wrap">{msg.message}</p>
                    {msg.attachment_url && (
                      <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-block mb-3">
                        <img src={msg.attachment_url} alt="attachment" className="max-h-40 rounded-lg border border-[#1A2030]" />
                      </a>
                    )}
                    {msg.admin_reply && (
                      <div className="bg-[#0A0E18] rounded-lg p-3 mb-3 border-l-2 border-[#EC008C]">
                        <p className="text-xs text-[#EC008C] font-semibold mb-1">Your Reply</p>
                        <p className="text-sm text-white whitespace-pre-wrap">{msg.admin_reply}</p>
                      </div>
                    )}
                    {!msg.admin_reply && (
                      replyingTo === msg.support_id ? (
                        <div className="flex gap-2">
                          <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." className="flex-1 bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white" data-testid={`reply-input-${msg.support_id}`} />
                          <Button onClick={() => sendReply(msg.support_id)} size="sm" className="bg-[#EC008C] text-[#000000]" data-testid={`send-reply-${msg.support_id}`}>Send</Button>
                          <Button onClick={() => setReplyingTo(null)} size="sm" variant="outline" className="border-[#1A2030] text-[#8A92A0]">Cancel</Button>
                        </div>
                      ) : (
                        <Button onClick={() => setReplyingTo(msg.support_id)} size="sm" className="bg-[#1A2030] text-white hover:bg-[#3F3F46]" data-testid={`open-reply-${msg.support_id}`}>Reply</Button>
                      )
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "ai" && (
            aiSessions.length === 0 ? (
              <div className="card p-12 text-center text-[#8A92A0]">No AI chat conversations logged yet.</div>
            ) : (
              <div className="space-y-3">
                {aiSessions.map(s => (
                  <div key={s.session_id} className="card p-5" data-testid={`ai-session-${s.session_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-white font-semibold">{s.authed_email || "Anonymous visitor"}</p>
                        <p className="text-xs text-[#6A7280]">Session: <span className="font-mono">{s.session_id}</span></p>
                        {s.ip && <p className="text-xs text-[#6A7280]">IP: {s.ip}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#EC008C]">{s.exchanges.length} exchange{s.exchanges.length === 1 ? "" : "s"}</p>
                        <p className="text-xs text-[#6A7280]">{new Date(s.last_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedSession(expandedSession === s.session_id ? null : s.session_id)}
                      className="text-sm text-[#EC008C] hover:text-[#FF3399] mt-2"
                      data-testid={`toggle-ai-session-${s.session_id}`}
                    >
                      {expandedSession === s.session_id ? "Hide conversation" : "View conversation"}
                    </button>
                    {expandedSession === s.session_id && (
                      <div className="mt-3 space-y-3 border-t border-[#1A2030] pt-3">
                        {s.exchanges.map(ex => (
                          <div key={ex.log_id} className="space-y-2">
                            <div className="bg-[#0A0E18] rounded-lg p-3 border-l-2 border-[#8A92A0]">
                              <p className="text-xs text-[#6A7280] mb-1">User · {new Date(ex.created_at).toLocaleString()}</p>
                              <p className="text-sm text-white whitespace-pre-wrap">{ex.user_message}</p>
                            </div>
                            <div className="bg-[#0A0E18] rounded-lg p-3 border-l-2 border-[#EC008C]">
                              <p className="text-xs text-[#EC008C] mb-1">AI assistant</p>
                              <p className="text-sm text-white whitespace-pre-wrap">{ex.ai_response}</p>
                              {ex.error && <p className="text-xs text-[#D14A85] mt-1">⚠ {ex.error}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "users" && (
            <div className="space-y-3">
              <div className="card p-4 flex flex-wrap gap-3">
                <input
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && fetchUsers(userSearch, userRole)}
                  placeholder="Search by email or name..."
                  className="flex-1 min-w-[200px] bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="admin-user-search"
                />
                <select
                  value={userRole}
                  onChange={e => { setUserRole(e.target.value); fetchUsers(userSearch, e.target.value); }}
                  className="bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="admin-user-role-filter"
                >
                  <option value="">All roles</option>
                  <option value="fan">Supporters</option>
                  <option value="artist">Artists</option>
                </select>
                <Button onClick={() => fetchUsers(userSearch, userRole)} size="sm" className="bg-[#EC008C] text-[#000000]" data-testid="admin-user-search-btn">Search</Button>
                <Button onClick={downloadUsersCsv} size="sm" className="bg-[#0A0E18] text-[#EC008C] border border-[#EC008C]/40 hover:bg-[#EC008C]/10" data-testid="admin-users-csv-btn">
                  Export CSV
                </Button>
              </div>
              {users.length === 0 ? (
                <div className="card p-12 text-center text-[#8A92A0]">No users found.</div>
              ) : (
                <div className="card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[#8A92A0] border-b border-[#1A2030]">
                        <th className="p-3">Name</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Role</th>
                        <th className="p-3">Joined</th>
                        <th className="p-3">Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.user_id} className="border-b border-[#1A2030] hover:bg-[#0A0E18]" data-testid={`admin-user-row-${u.user_id}`}>
                          <td className="p-3 text-white">
                            {u.name}
                            {u.artist_profile?.is_verified && <BadgeCheck className="inline w-4 h-4 ml-1 text-[#EC008C]" />}
                          </td>
                          <td className="p-3 text-[#8A92A0]">{u.email}</td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 rounded text-xs ${u.artist_profile ? "bg-[#EC008C]/10 text-[#EC008C]" : "bg-[#1A2030] text-[#8A92A0]"}`}>
                              {u.artist_profile ? "Artist" : "Fan"}
                            </span>
                          </td>
                          <td className="p-3 text-[#6A7280]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                          <td className="p-3 text-[#8A92A0] text-xs">
                            {u.follow_count > 0 && <span className="mr-2">{u.follow_count} follows</span>}
                            {u.ticket_count > 0 && <span className="mr-2">{u.ticket_count} tickets</span>}
                            {u.last_session_at && <span className="text-[#6A7280]">Last: {new Date(u.last_session_at).toLocaleDateString()}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "reports" && (
            <div className="space-y-3">
              <div className="card p-4 flex gap-3 items-center">
                <span className="text-sm text-[#8A92A0]">Filter:</span>
                {["pending", "resolved", "dismissed", "all"].map(s => (
                  <button
                    key={s}
                    onClick={() => { setReportStatus(s); fetchReports(s); }}
                    className={`px-3 py-1 rounded-full text-xs ${reportStatus === s ? "bg-[#EC008C] text-[#000000]" : "bg-[#0A0E18] text-[#8A92A0] border border-[#1A2030]"}`}
                    data-testid={`report-filter-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {reports.length === 0 ? (
                <div className="card p-12 text-center text-[#8A92A0]">No reports.</div>
              ) : (
                <div className="space-y-3">
                  {reports.map(r => (
                    <div key={r.report_id} className="card p-5" data-testid={`report-${r.report_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white font-semibold capitalize">{r.reason} · {r.content_type}</p>
                          <p className="text-xs text-[#6A7280]">Reported by {r.reporter_name || r.reporter_email || "user"} · {new Date(r.created_at).toLocaleString()}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs capitalize ${
                          r.status === 'pending' ? 'bg-[#D14A85]/10 text-[#D14A85]' :
                          r.status === 'resolved' ? 'bg-[#8DB87A]/10 text-[#8DB87A]' :
                          'bg-[#1A2030] text-[#8A92A0]'
                        }`}>{r.status}</span>
                      </div>
                      {r.details && <p className="text-sm text-[#8A92A0] mb-3 italic">"{r.details}"</p>}
                      {r.content_snippet && (
                        <div className="bg-[#0A0E18] rounded-lg p-3 mb-3 border border-[#1A2030]">
                          <p className="text-xs text-[#6A7280] mb-1">Reported {r.content_type}</p>
                          {r.content_snippet.text && <p className="text-sm text-white whitespace-pre-wrap">{r.content_snippet.text}</p>}
                          {r.content_snippet.image_url && <img src={r.content_snippet.image_url} alt="" className="max-h-32 mt-2 rounded" />}
                          {r.content_snippet.author && <p className="text-xs text-[#EC008C] mt-2">by {r.content_snippet.author}</p>}
                          {r.content_snippet.email && <p className="text-xs text-[#8A92A0]">{r.content_snippet.email}</p>}
                        </div>
                      )}
                      {r.status === "pending" && (
                        <div className="flex gap-2">
                          {r.content_type === "post" && (
                            <Button onClick={() => decideReport(r.report_id, "remove_content")} size="sm" className="bg-[#D14A85] text-white hover:bg-[#A04545]" data-testid={`remove-${r.report_id}`}>
                              Remove content
                            </Button>
                          )}
                          <Button onClick={() => decideReport(r.report_id, "dismiss")} size="sm" className="bg-[#1A2030] text-[#8A92A0] hover:bg-[#2A3040]" data-testid={`dismiss-${r.report_id}`}>
                            Dismiss
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "audit" && (
            auditLogs.length === 0 ? (
              <div className="card p-12 text-center text-[#8A92A0]">No audit events yet.</div>
            ) : (
              <div className="card overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#8A92A0] border-b border-[#1A2030]">
                      <th className="p-3">When</th>
                      <th className="p-3">Action</th>
                      <th className="p-3">Actor</th>
                      <th className="p-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.log_id} className="border-b border-[#1A2030] hover:bg-[#050810]" data-testid={`audit-row-${log.log_id}`}>
                        <td className="p-3 text-[#8A92A0] text-xs whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                        <td className="p-3"><span className="px-2 py-0.5 rounded bg-[#EC008C]/10 text-[#EC008C] text-xs font-mono">{log.action}</span></td>
                        <td className="p-3 text-[#8A92A0]">{log.actor_email || log.actor_id || "—"}</td>
                        <td className="p-3 text-[#8A92A0] text-xs">
                          {log.target_id && <div>target: <span className="font-mono">{log.target_id}</span></div>}
                          {log.meta && Object.keys(log.meta).length > 0 && <div className="text-[10px] text-[#4A5260]">{JSON.stringify(log.meta)}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const { user, artistProfile, token, logout, refreshArtistProfile, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);

  // Request browser notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      // Delay permission request to avoid immediate popup
      const timer = setTimeout(() => {
        Notification.requestPermission();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (artistProfile) {
      fetchStats();
      fetchNotifications();
    }
    // Poll for new notifications every 30s
    const interval = setInterval(() => {
      if (token) fetchNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [artistProfile, token]);

  // Show browser notification when new unread count increases
  useEffect(() => {
    if (unreadCount > prevUnreadCount && prevUnreadCount >= 0 && "Notification" in window && Notification.permission === "granted") {
      const newCount = unreadCount - prevUnreadCount;
      const latest = notifications.find(n => !n.read);
      if (latest) {
        new Notification("StardomFans.com", {
          body: latest.title + (latest.message ? ": " + latest.message : ""),
          icon: "/favicon.ico",
          tag: "stardomfans-notif"
        });
      }
    }
    setPrevUnreadCount(unreadCount);
  }, [unreadCount]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error("Failed to mark notifications read:", e);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/artist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Overview" },
    { path: "/dashboard/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/dashboard/growth", icon: TrendingUp, label: "Growth" },
    { path: "/dashboard/weekly", icon: CalendarRange, label: "Weekly Report" },
    { path: "/dashboard/payouts", icon: Wallet, label: "Payouts" },
    { path: "/dashboard/content", icon: Music, label: user?.is_admin ? "Content" : "Upload Content" },
    { path: "/dashboard/events", icon: Calendar, label: user?.is_admin ? "Events" : "Create Event" },
    { path: "/dashboard/products", icon: ShoppingBag, label: user?.is_admin ? "Products" : "Add Product" },
    ...(!user?.is_admin ? [{ path: "/seller/orders", icon: Package, label: "Shipping" }] : []),
    { path: "/dashboard/promo-codes", icon: Tag, label: "Promo Codes" },
    { path: "/dashboard/subscriptions", icon: Heart, label: user?.is_admin ? "Subscriptions" : "Manage Subs" },
    { path: "/dashboard/experiences", icon: Star, label: "Experiences" },
    { path: "/dashboard/ai-messages", icon: Sparkles, label: "AI Messages" },
    { path: "/dashboard/collectibles", icon: Star, label: "Collectibles" },
    { path: "/dashboard/promo-kit", icon: Sparkles, label: "Promo Kit" },
    { path: "/dashboard/referrals", icon: Gift, label: "Referrals" },
    { path: "/messages", icon: MessageCircle, label: "Messages" },
    { path: "/dashboard/profile", icon: User, label: user?.is_admin ? "Profile" : "Edit Profile" },
    { path: "/security/sessions", icon: Shield, label: "Sessions" },
    { path: "/dashboard/security", icon: Shield, label: "Security" },
    { path: "/dashboard/trust-safety", icon: Shield, label: "Trust & Safety" },
    { path: "/dashboard/settings", icon: Bell, label: "Settings" },
    ...(user?.is_admin ? [
      { path: "/dashboard/admin-inmail", icon: Mail, label: "Admin In-Mail" },
      { path: "/dashboard/admin", icon: Shield, label: "Admin" },
      { path: "/admin/verifications", icon: ShieldCheck, label: "Verification Portal" },
      { path: "/admin/cra", icon: Landmark, label: "Tax & Reporting" },
      { path: "/dashboard/support", icon: MessageCircle, label: "Support Inbox" },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-[#050810] workspace-surface" data-testid="artist-dashboard">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="flex items-center justify-between pl-4 pr-16 py-3">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="SF" className="w-12 h-12 object-contain" style={{mixBlendMode:"lighten"}} />
          </Link>
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2"
              data-testid="notification-bell-mobile"
            >
              <Bell className="w-6 h-6 text-white" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D14A85] rounded-full text-xs text-white flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
              data-testid="mobile-menu-btn"
              aria-label={mobileMenuOpen ? "Close studio navigation" : "Open studio navigation"}
              aria-expanded={mobileMenuOpen}
              aria-controls="studio-mobile-navigation"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>

        {/* Notifications Dropdown */}
        {showNotifications && (
          <div className="absolute top-16 right-4 left-4 bg-[#0A0E18] border border-[#1A2030] rounded-xl shadow-xl max-h-96 overflow-y-auto z-50">
            <div className="p-4 border-b border-[#1A2030] flex items-center justify-between">
              <h3 className="font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#EC008C]">Mark all read</button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-[#8A92A0]">No notifications yet</div>
            ) : (
              <div className="divide-y divide-[#27272A]">
                {notifications.map((notif) => (
                  <div 
                    key={notif.notification_id} 
                    className={`p-4 ${!notif.read ? 'bg-[#EC008C]/5' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notif.type === 'sale' ? 'bg-[#8DB87A]/10' :
                        notif.type === 'subscription' ? 'bg-[#D14A85]/10' :
                        notif.type === 'payout' ? 'bg-[#EC008C]/10' :
                        'bg-[#FF6BC5]/10'
                      }`}>
                        {notif.type === 'sale' && <DollarSign className="w-4 h-4 text-[#8DB87A]" />}
                        {notif.type === 'subscription' && <Heart className="w-4 h-4 text-[#D14A85]" />}
                        {notif.type === 'payout' && <Banknote className="w-4 h-4 text-[#EC008C]" />}
                        {notif.type === 'system' && <Bell className="w-4 h-4 text-[#FF6BC5]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{notif.title}</p>
                        <p className="text-xs text-[#8A92A0]">{notif.message}</p>
                        <p className="text-xs text-[#8A92A0] mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                      </div>
                      {notif.amount && (
                        <span className="text-[#EC008C] font-semibold">${notif.amount}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav id="studio-mobile-navigation" className="px-4 pb-4 space-y-4 max-h-[calc(100dvh-88px)] overflow-y-auto" aria-label="Artist Studio" data-testid="studio-mobile-navigation">
            <GroupedNavigation groups={groupArtistNavigation(navItems, { storeFirst: !user?.is_admin })} pathname={location.pathname} scope="studio-mobile" onNavigate={() => setMobileMenuOpen(false)} />
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#D14A85] hover:bg-[#D14A85]/10 w-full"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </nav>
        )}
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-[#0A0E18] border-r border-[#1A2030]">
        <div className="p-6">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-14 h-14 object-contain" style={{mixBlendMode:"lighten"}} />
          </Link>
        </div>

        <nav className="flex-1 px-4 pb-5 overflow-y-auto scrollbar-thin scrollbar-thumb-[#27272A] scrollbar-track-transparent" aria-label="Artist Studio" data-testid="studio-desktop-navigation">
          <GroupedNavigation groups={groupArtistNavigation(navItems, { storeFirst: !user?.is_admin })} pathname={location.pathname} scope="studio-desktop" />
        </nav>

        <div className="p-4 border-t border-[#1A2030]">
          {/* Desktop Notification Bell */}
          <div className="mb-4 relative">
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-[#050810] transition-colors"
              data-testid="notification-bell-desktop"
            >
              <Bell className="w-5 h-5 text-[#8A92A0]" />
              <span className="text-sm text-[#8A92A0]">Notifications</span>
              {unreadCount > 0 && (
                <span className="ml-auto bg-[#D14A85] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            
            {/* Desktop Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#0A0E18] border border-[#1A2030] rounded-xl shadow-xl max-h-96 overflow-y-auto z-50">
                <div className="p-4 border-b border-[#1A2030] flex items-center justify-between">
                  <h3 className="font-semibold text-white">Notifications</h3>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-xs text-[#EC008C]">Mark all read</button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-[#8A92A0]">No notifications yet</div>
                ) : (
                  <div className="divide-y divide-[#27272A]">
                    {notifications.map((notif) => (
                      <div 
                        key={notif.notification_id} 
                        className={`p-4 ${!notif.read ? 'bg-[#EC008C]/5' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            notif.type === 'sale' ? 'bg-[#8DB87A]/10' :
                            notif.type === 'subscription' ? 'bg-[#D14A85]/10' :
                            notif.type === 'payout' ? 'bg-[#EC008C]/10' :
                            'bg-[#FF6BC5]/10'
                          }`}>
                            {notif.type === 'sale' && <DollarSign className="w-4 h-4 text-[#8DB87A]" />}
                            {notif.type === 'subscription' && <Heart className="w-4 h-4 text-[#D14A85]" />}
                            {notif.type === 'payout' && <Banknote className="w-4 h-4 text-[#EC008C]" />}
                            {notif.type === 'system' && <Bell className="w-4 h-4 text-[#FF6BC5]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white">{notif.title}</p>
                            <p className="text-xs text-[#8A92A0]">{notif.message}</p>
                            <p className="text-xs text-[#8A92A0] mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#1A2030] overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-full h-full p-2 text-[#8A92A0]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{artistProfile?.stage_name || user?.name}</p>
              <p className="text-xs text-[#8A92A0] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#8A92A0] hover:text-[#D14A85] transition-colors w-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-24 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8 max-w-6xl">
          {/* Profile-first banner */}
          {!artistProfile && location.pathname !== "/dashboard/profile" && location.pathname !== "/dashboard/admin" && location.pathname !== "/dashboard/trust-safety" && location.pathname !== "/dashboard/admin-inmail" && location.pathname !== "/dashboard/security" && (
            <div className="card p-6 mb-6 border-[#EC008C]/30 bg-[#EC008C]/5" data-testid="create-profile-banner">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#EC008C]/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-[#EC008C]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-1">Create Your Artist Profile First</h3>
                  <p className="text-[#8A92A0] text-sm">You need an artist profile before you can create events, upload content, add products, or set up subscriptions.</p>
                </div>
                <Button
                  onClick={() => navigate("/dashboard/profile")}
                  className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] whitespace-nowrap"
                  data-testid="go-to-profile-btn"
                >
                  <User className="w-4 h-4 mr-2" /> Create Profile
                </Button>
              </div>
            </div>
          )}
          <Routes>
            <Route index element={<DashboardOverview stats={stats} artistProfile={artistProfile} isAdmin={!!user?.is_admin} token={token} />} />
            <Route path="analytics" element={<AnalyticsDashboard token={token} />} />
            <Route path="growth" element={<GrowthHub token={token} />} />
            <Route path="security" element={<SecuritySettings token={token} user={user} onRefreshUser={refreshUser} />} />
            <Route path="weekly" element={<WeeklySummary token={token} />} />
            <Route path="payouts" element={<PayoutsManagement token={token} />} />
            <Route path="content" element={<ContentManagement token={token} />} />
            <Route path="events" element={<EventsManagement token={token} />} />
            <Route path="products" element={<ProductsManagement token={token} />} />
            <Route path="promo-codes" element={<PromoCodesManagement token={token} />} />
            <Route path="subscriptions" element={<SubscriptionsManagement token={token} />} />
            <Route path="experiences" element={<ExperiencesManagement token={token} />} />
            <Route path="ai-messages" element={<AIMessagesManagement token={token} />} />
          <Route path="collectibles" element={<DigitalCollectiblesManagement token={token} />} />
            <Route path="promo-kit" element={<PromoKit token={token} />} />
            <Route path="referrals" element={<ReferralsTab token={token} />} />
            <Route path="admin-inmail" element={<AdminInmailViewer token={token} />} />
            <Route path="admin" element={<AdminPanel token={token} user={user} />} />
            <Route path="trust-safety" element={<TrustSafetyPanel token={token} isAdmin={!!user?.is_admin} />} />
            <Route path="support" element={<SupportInbox token={token} />} />
            <Route path="settings" element={<NotificationSettings token={token} />} />
            <Route path="profile" element={
              <ProfileManagement 
                token={token} 
                artistProfile={artistProfile} 
                onUpdate={refreshArtistProfile}
              />
            } />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
